import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../context/AuthContext';
import {
  ChevronRight, ChevronDown, RefreshCw, Filter, AlertCircle, Layers, Wallet, Download,
  ArrowUp, ArrowDown, ArrowUpDown, TrendingUp, CalendarDays, FileText, Copy, Check, Lock
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, LabelList,
} from 'recharts';
import { Card } from '../../components/ui/Card';
import DateRangeFilter from '../../components/filters/DateRangeFilter';
import { formatDateYMD, computePresetRange, describeRange } from '../../utils/dateRangeHelpers';
import { getBrandGroups, matchBrand, HIGH5_BRAND_ID } from '../../config/brandGroups';
import { buildNetSalesCommentary, commentaryToText } from '../../utils/high5NetSalesCommentary';

// Otomatik haftalık yorum yalnız ajans içi (heyglowy.com) kullanıcılara görünür —
// müşteriye giden yorumun taslağı, marka kullanıcısı görmemeli.
const isHeyglowyUser = (user) => /@heyglowy\.com$/i.test(String(user?.email || '').trim());

const API_BASE = 'https://api.glowytics.com';

const STATUS_LABEL = {
  ENABLED: 'Aktif',
  ACTIVE: 'Aktif',
  PAUSED: 'Duraklatıldı',
  REMOVED: 'Silinmiş',
};

const PLATFORM_BADGE = {
  GOOGLE_ADS: {
    label: 'Google',
    cls: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    dot: 'bg-yellow-500',
  },
  META: {
    label: 'Meta',
    cls: 'bg-blue-50 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
  },
};

const BUDGET_SOURCE_LABEL = {
  campaign: 'Kampanya',
  adset_sum: 'Ad Set toplamı',
  none: 'Bütçe yok',
};

// Marka portföyü kovaları + matchBrand artık TEK KAYNAK: src/config/brandGroups.js
// (Overview2 "Marka Bazlı Harcama" tablosuyla aynı listeyi paylaşır.)
// getBrandGroups, matchBrand yukarıda import edildi.

// weekly-live'ın ham satırlarını (week × campaign_name × cost) marka kovalarına
// çevirir. Kartlar bu şekli bekler: [{ week, brands: { KEY: cost }, total }].
// Public rapor sayfası (pages/public/High5NetSalesPublic.js) da bunu kullanır —
// kova mantığı tek yerde kalsın diye export edildi.
export function bucketWeeklyRowsByBrand(rows, brandGroups) {
  return bucketCostRowsByBrand(rows, brandGroups, 'week');
}

// monthly-live'ın ikizi: aynı kova mantığı, anahtar hafta değil takvim ayı
// ('YYYY-MM'). Şekil: [{ month, brands: { KEY: cost }, total }].
export function bucketMonthlyRowsByBrand(rows, brandGroups) {
  return bucketCostRowsByBrand(rows, brandGroups, 'month');
}

// Hafta/ay ortak gövdesi — kova mantığı (matchBrand + BRAND_GROUPS) tek yerde
// kalsın diye ayrıldı; iki görünüm arasında marka eşlemesi ayrışmasın.
function bucketCostRowsByBrand(rows, brandGroups, keyField) {
  const BRAND_GROUPS = brandGroups || [];
  const byKey = {};
  for (const row of rows || []) {
    const k = row[keyField];
    if (!k) continue;
    if (!byKey[k]) {
      byKey[k] = { [keyField]: k, brands: {}, total: 0 };
      BRAND_GROUPS.forEach(b => { byKey[k].brands[b.key] = 0; });
      byKey[k].brands['Other'] = 0;
    }
    let matched = 'Other';
    for (const b of BRAND_GROUPS) {
      if (matchBrand(row.campaign_name, b.match || b.key)) { matched = b.key; break; }
    }
    const cost = Number(row.cost || 0);
    byKey[k].brands[matched] = (byKey[k].brands[matched] || 0) + cost;
    byKey[k].total += cost;
  }
  return Object.values(byKey).sort((a, b) => String(b[keyField]).localeCompare(String(a[keyField])));
}

const fmtMoney = (v, currency = '₺') => {
  if (v == null || isNaN(Number(v))) return '—';
  return `${currency}${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
};

const fmtNumber = (v) => {
  if (v == null || isNaN(Number(v))) return '—';
  const n = Number(v);
  // Show decimals only if fractional conversions (Google Ads allows fractional)
  return n.toLocaleString('tr-TR', {
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });
};

// Format a 0..1 ratio (or 0..100 number) as "42,3%".
const fmtPct = (v, denom) => {
  const n = denom == null ? Number(v) : (Number(denom) > 0 ? Number(v) / Number(denom) : 0);
  if (!isFinite(n) || isNaN(n)) return '—';
  return `${(n * 100).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%`;
};

const statusBadge = (status) => {
  const s = (status || '').toUpperCase();
  const isActive = s === 'ENABLED' || s === 'ACTIVE';
  const isPaused = s === 'PAUSED';
  const cls = isActive
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : isPaused
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : isPaused ? 'bg-amber-500' : 'bg-gray-400'}`} />
      {STATUS_LABEL[s] || s || '—'}
    </span>
  );
};

export default function UnifiedCampaigns() {
  const { selectedBrand, user } = useAuth();
  // Brand-label keyword list, resolved per brand (each retailer's portfolio differs).
  const BRAND_GROUPS = useMemo(() => getBrandGroups(selectedBrand?.id), [selectedBrand?.id]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Default 'ALL' — paused-but-had-spend campaigns must show in brand totals
  // (otherwise spend from campaigns paused mid-window vanishes). User can still
  // filter via the dropdown.
  const [status, setStatus] = useState('ALL');
  const [platform, setPlatform] = useState('ALL');
  const [expanded, setExpanded] = useState(() => new Set());
  // Date filter — drives live cost aggregation on both platforms. Default to
  // "today" so the page immediately shows how much of today's budget is spent.
  const [dateRange, setDateRange] = useState('today');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  // BQ item_sales aggregated by brand keyword for the active date range.
  // Map: { 'ASICS': { qty, revenue }, ... } keyed by BRAND_GROUPS.key.
  const [bqSales, setBqSales] = useState(null);

  // Weekly brand-bucketed cost for the last 120 days. Independent of the
  // page's date range filter — the user wants a fixed historical pivot.
  const [weeklyByBrand, setWeeklyByBrand] = useState(null);
  const [weeklyByBrandLoading, setWeeklyByBrandLoading] = useState(false);

  // Weekly BQ revenue per item_brand, also fixed 120-day window.
  const [weeklyBQRevenue, setWeeklyBQRevenue] = useState(null);
  const [weeklyBQRevenueLoading, setWeeklyBQRevenueLoading] = useState(false);

  // Haftalık matrisin GÜN kırılımlı ikizi. Ayrı pencere (30 gün) — haftalık
  // 120 günü günlüğe çevirmek hem API'yi hem tabloyu gereksiz şişirirdi.
  const [dailyByBrand, setDailyByBrand] = useState(null);
  const [dailyByBrandLoading, setDailyByBrandLoading] = useState(false);
  const [dailyBQRevenue, setDailyBQRevenue] = useState(null);
  const [dailyBQRevenueLoading, setDailyBQRevenueLoading] = useState(false);

  // ERP'den yüklenen haftalık net satış (High5). Kaynak: /high5/net-sales sayfası.
  const [weeklyNetSales, setWeeklyNetSales] = useState(null);
  const [weeklyNetSalesLoading, setWeeklyNetSalesLoading] = useState(false);

  // GA4 (BQ) campaign-level revenue for unmatched (brand-less) ad campaigns.
  // Honors the page's selected date range. Brand filtering is applied client-side
  // with the same matchBrand/BRAND_GROUPS used by the spend pivot's "Diğer" bucket.
  const [ga4Campaigns, setGa4Campaigns] = useState(null);
  const [ga4CampaignsLoading, setGa4CampaignsLoading] = useState(false);
  const [budgetPlan, setBudgetPlan] = useState(null);
  const [budgetPlanLoading, setBudgetPlanLoading] = useState(false);

  // Resolve the active preset/custom dates to YYYY-MM-DD strings for the API.
  const { startDate: apiStartDate, endDate: apiEndDate } = useMemo(() => {
    if (dateRange === 'custom') {
      return {
        startDate: customStart ? formatDateYMD(customStart) : null,
        endDate: customEnd ? formatDateYMD(customEnd) : null,
      };
    }
    const range = computePresetRange(dateRange);
    if (!range || !range.startDate || !range.endDate) return { startDate: null, endDate: null };
    return {
      startDate: formatDateYMD(range.startDate),
      endDate: formatDateYMD(range.endDate),
    };
  }, [dateRange, customStart, customEnd]);

  const fetchData = useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('status', status);
      qs.set('platform', platform);
      if (apiStartDate && apiEndDate) {
        qs.set('startDate', apiStartDate);
        qs.set('endDate', apiEndDate);
      }
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/unified-campaigns?${qs.toString()}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id,
        },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${r.status}`);
      }
      const json = await r.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id, status, platform, apiStartDate, apiEndDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch BQ item_sales aggregated by brand for the same date range and roll
  // it up into BRAND_GROUPS keys (word-boundary match, like the campaign
  // bucketing). Reuses the bq-analytics endpoint that powers
  // /analytics/bq-item-sales > Brand Summary.
  useEffect(() => {
    if (!selectedBrand?.id || !apiStartDate || !apiEndDate) {
      setBqSales(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({
          startDate: apiStartDate,
          endDate: apiEndDate,
          groupBy: 'brand',
        });
        const r = await fetch(`${API_BASE}/api/bq-analytics/item-sales-by-brand?${qs.toString()}`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': selectedBrand.id },
        });
        if (!r.ok) { if (!cancelled) setBqSales(null); return; }
        const json = await r.json();
        if (cancelled) return;
        const map = {};
        for (const row of (json.rows || [])) {
          for (const b of BRAND_GROUPS) {
            if (matchBrand(row.label, b.match || b.key)) {
              if (!map[b.key]) map[b.key] = { qty: 0, revenue: 0 };
              map[b.key].qty += Number(row.totalQty) || 0;
              map[b.key].revenue += Number(row.totalRevenue) || 0;
              break;
            }
          }
        }
        setBqSales(map);
      } catch (_) {
        if (!cancelled) setBqSales(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBrand?.id, apiStartDate, apiEndDate]);

  // Last 120 days, weekly cost pivot bucketed by BRAND_GROUPS keys + Other.
  // LIVE — calls Google Ads + Meta APIs server-side (15-30s on cold cache).
  // Reused via 5-minute backend cache. Triggered on brand change or Yenile.
  const fetchWeeklyByBrand = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setWeeklyByBrand(null); return; }
    setWeeklyByBrandLoading(true);
    try {
      const qs = new URLSearchParams({ days: '120' });
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/unified-campaigns/weekly-live?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setWeeklyByBrand([]); return; }
      const json = await r.json();
      setWeeklyByBrand(bucketWeeklyRowsByBrand(json.rows, BRAND_GROUPS));
    } catch (_) {
      setWeeklyByBrand([]);
    } finally {
      setWeeklyByBrandLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchWeeklyByBrand(); }, [fetchWeeklyByBrand]);

  // Last 120 days, weekly BQ revenue pivot from bq_ga4_item_sales_daily.
  // Brands listed dynamically; no spend column.
  const fetchWeeklyBQRevenue = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setWeeklyBQRevenue(null); return; }
    setWeeklyBQRevenueLoading(true);
    try {
      const qs = new URLSearchParams({ days: '120' });
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/unified-campaigns/weekly-bq-revenue?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setWeeklyBQRevenue({ brands: [], rows: [], monthlyRows: [] }); return; }
      const json = await r.json();
      // monthlyRows/monthGrain de TAŞINMALI: aylık görünüm bunu kullanıyor.
      // Burada kırpılırsa kart sessizce eski haftadan-ay türetmesine düşer.
      setWeeklyBQRevenue({
        brands: json.brands || [],
        rows: json.rows || [],
        monthlyRows: json.monthlyRows || null,
        monthGrain: json.monthGrain || null,
      });
    } catch (_) {
      setWeeklyBQRevenue({ brands: [], rows: [], monthlyRows: [] });
    } finally {
      setWeeklyBQRevenueLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchWeeklyBQRevenue(); }, [fetchWeeklyBQRevenue]);

  // ---- ROAS matrisinin AY görünümü: 12 ay, TEMBEL yüklenir -----------------
  // Sayfa açılışında çekilmiyor. Harcama tarafı canlı Google Ads + Meta çağrısı;
  // kimse "Ay"a basmadan 365 günlük ikinci bir tur atmanın anlamı yok.
  // Pencere 365 gün: 120 günde ancak 4-5 ay çıkıyor, aylık görünüm o kadarla
  // trend göstermiyor.
  const [monthlyByBrand, setMonthlyByBrand] = useState(null);
  const [monthlyBQRevenue, setMonthlyBQRevenue] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const monthlyRequested = React.useRef(false);

  const fetchMonthly = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setMonthlyByBrand(null); setMonthlyBQRevenue(null); return; }
    setMonthlyLoading(true);
    try {
      const qs = new URLSearchParams({ days: '365' });
      if (opts.force) qs.set('refresh', '1');
      const head = { credentials: 'include', headers: { 'X-Brand-ID': selectedBrand.id } };
      // Harcama ve ciro AYNI pencereden — biri 365 diğeri 120 günse en eski ayın
      // ROAS'ı yarım harcamayı tam ciroya böler.
      const [rCost, rRev] = await Promise.all([
        fetch(`${API_BASE}/api/unified-campaigns/monthly-live?${qs.toString()}`, head),
        fetch(`${API_BASE}/api/unified-campaigns/weekly-bq-revenue?${qs.toString()}`, head),
      ]);
      setMonthlyByBrand(rCost.ok
        ? bucketMonthlyRowsByBrand((await rCost.json()).rows, BRAND_GROUPS)
        : []);
      if (rRev.ok) {
        const j = await rRev.json();
        setMonthlyBQRevenue({ monthlyRows: j.monthlyRows || null, monthGrain: j.monthGrain || null });
      } else {
        setMonthlyBQRevenue({ monthlyRows: [], monthGrain: null });
      }
    } catch (_) {
      setMonthlyByBrand([]);
      setMonthlyBQRevenue({ monthlyRows: [], monthGrain: null });
    } finally {
      setMonthlyLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  // Kart "Ay"a geçtiğinde çağırır. İlk geçişte çeker, sonraki geçişlerde sessiz.
  const ensureMonthly = React.useCallback(() => {
    if (monthlyRequested.current) return;
    monthlyRequested.current = true;
    fetchMonthly();
  }, [fetchMonthly]);

  // ---- ROAS matrisinin TARİH görünümü ------------------------------------
  // Serbest aralık + hemen öncesindeki EŞİT UZUNLUKTA dönem. İki kolon çıkıyor;
  // tek kolonda ROAS trend oku hiçbir şeye kıyaslanamıyordu.
  // Kart hangi tarihleri istediğini kendi tutuyor, veri erişimi burada kalıyor.
  const fetchRangeRoas = React.useCallback(async ({ start, end, prevStart, prevEnd }) => {
    if (!selectedBrand?.id) return null;
    const head = { credentials: 'include', headers: { 'X-Brand-ID': selectedBrand.id } };
    const q = (o) => new URLSearchParams(o).toString();
    const al = async (url) => { const r = await fetch(url, head); return r.ok ? r.json() : null; };

    const [c1, c2, r1, r2] = await Promise.all([
      al(`${API_BASE}/api/unified-campaigns/range-live?${q({ start, end })}`),
      al(`${API_BASE}/api/unified-campaigns/range-live?${q({ start: prevStart, end: prevEnd })}`),
      al(`${API_BASE}/api/unified-campaigns/range-bq-revenue?${q({ start, end })}`),
      al(`${API_BASE}/api/unified-campaigns/range-bq-revenue?${q({ start: prevStart, end: prevEnd })}`),
    ]);

    // Harcamayı kampanya adından marka kovasına indir — hafta/ay ile AYNI eşleme.
    const kova = (json) => {
      const out = {};
      BRAND_GROUPS.forEach(b => { out[b.key] = 0; });
      out.Other = 0;
      for (const row of json?.rows || []) {
        let matched = 'Other';
        for (const b of BRAND_GROUPS) {
          if (matchBrand(row.campaign_name, b.match || b.key)) { matched = b.key; break; }
        }
        out[matched] += Number(row.cost || 0);
      }
      return out;
    };

    // Ciro HAM item_brand etiketiyle dönüyor; kart onu kendi içinde kovalıyor
    // (hafta/ay ile aynı kod yolu) — burada dokunma.
    return {
      cost: [
        { period: 'cur', brands: kova(c1) },
        { period: 'prev', brands: kova(c2) },
      ],
      revenue: [
        { period: 'cur', brands: r1?.byBrand || {} },
        { period: 'prev', brands: r2?.byBrand || {} },
      ],
      failed: !c1 || !c2 || !r1 || !r2,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  // Marka değişince aylık veri bayatlar — sıfırla, kullanıcı tekrar Ay'a
  // basarsa yeni markanınki çekilsin.
  useEffect(() => {
    monthlyRequested.current = false;
    setMonthlyByBrand(null);
    setMonthlyBQRevenue(null);
  }, [selectedBrand?.id]);

  // Son 30 gün, GÜNLÜK harcama pivotu — fetchWeeklyByBrand ile aynı kova mantığı
  // (matchBrand + BRAND_GROUPS), tek farkı bucket anahtarı hafta değil gün.
  const fetchDailyByBrand = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setDailyByBrand(null); return; }
    setDailyByBrandLoading(true);
    try {
      const qs = new URLSearchParams({ days: '30' });
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/unified-campaigns/daily-live?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setDailyByBrand([]); return; }
      const json = await r.json();
      const byDay = {};
      for (const row of json.rows || []) {
        const d = row.date;
        if (!d) continue;
        if (!byDay[d]) {
          byDay[d] = { date: d, brands: {}, total: 0 };
          BRAND_GROUPS.forEach(b => { byDay[d].brands[b.key] = 0; });
          byDay[d].brands['Other'] = 0;
        }
        let matched = 'Other';
        for (const b of BRAND_GROUPS) {
          if (matchBrand(row.campaign_name, b.match || b.key)) { matched = b.key; break; }
        }
        const cost = Number(row.cost || 0);
        byDay[d].brands[matched] = (byDay[d].brands[matched] || 0) + cost;
        byDay[d].total += cost;
      }
      setDailyByBrand(Object.values(byDay).sort((a, b) => b.date.localeCompare(a.date)));
    } catch (_) {
      setDailyByBrand([]);
    } finally {
      setDailyByBrandLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  useEffect(() => { fetchDailyByBrand(); }, [fetchDailyByBrand]);

  // Son 30 gün, günlük BQ ciro pivotu (item_brand × gün).
  const fetchDailyBQRevenue = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setDailyBQRevenue(null); return; }
    setDailyBQRevenueLoading(true);
    try {
      const qs = new URLSearchParams({ days: '30' });
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/unified-campaigns/daily-bq-revenue?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setDailyBQRevenue({ brands: [], rows: [] }); return; }
      const json = await r.json();
      setDailyBQRevenue({ brands: json.brands || [], rows: json.rows || [] });
    } catch (_) {
      setDailyBQRevenue({ brands: [], rows: [] });
    } finally {
      setDailyBQRevenueLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchDailyBQRevenue(); }, [fetchDailyBQRevenue]);

  // ERP'den yüklenen haftalık gerçekleşen net satış (KDV hariç). Sadece High5'te
  // veri var; diğer markalarda endpoint boş döner ve kart hiç render edilmez.
  const fetchWeeklyNetSales = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setWeeklyNetSales(null); return; }
    setWeeklyNetSalesLoading(true);
    try {
      const qs = new URLSearchParams({ days: '120' });
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/high5-net-sales/weekly?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setWeeklyNetSales({ rows: [] }); return; }
      const json = await r.json();
      setWeeklyNetSales({ rows: json.rows || [] });
    } catch (_) {
      setWeeklyNetSales({ rows: [] });
    } finally {
      setWeeklyNetSalesLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchWeeklyNetSales(); }, [fetchWeeklyNetSales]);

  // Aylık hedef bütçe planı + gerçekleşen. Tarih filtresinden BAĞIMSIZ: plan ay
  // bazlı, seçili aralığa göre kırpılırsa hedefle kıyas anlamsızlaşır.
  const fetchBudgetPlan = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setBudgetPlan(null); return; }
    setBudgetPlanLoading(true);
    try {
      const qs = new URLSearchParams();
      if (opts.year) qs.set('year', String(opts.year));
      if (opts.month) qs.set('month', String(opts.month));
      const r = await fetch(`${API_BASE}/api/unified-campaigns/budget-plan?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setBudgetPlan(null); return; }
      setBudgetPlan(await r.json());
    } catch (_) {
      setBudgetPlan(null);
    } finally {
      setBudgetPlanLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchBudgetPlan(); }, [fetchBudgetPlan]);

  // GA4 campaign-level revenue (paid mediums only) for the selected date range.
  const fetchGa4Campaigns = React.useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setGa4Campaigns(null); return; }
    setGa4CampaignsLoading(true);
    try {
      const qs = new URLSearchParams();
      if (apiStartDate) qs.set('startDate', apiStartDate);
      if (apiEndDate) qs.set('endDate', apiEndDate);
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/unified-campaigns/ga4-unmatched-campaigns?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setGa4Campaigns({ campaigns: [], range: null }); return; }
      const json = await r.json();
      setGa4Campaigns({ campaigns: json.campaigns || [], range: json.range || null });
    } catch (_) {
      setGa4Campaigns({ campaigns: [], range: null });
    } finally {
      setGa4CampaignsLoading(false);
    }
  }, [selectedBrand?.id, apiStartDate, apiEndDate]);

  useEffect(() => { fetchGa4Campaigns(); }, [fetchGa4Campaigns]);

  // Per-brand breakdown: sum effectiveDailyBudget by brand keyword × platform.
  // A campaign is assigned to the first brand whose key is found in its name;
  // campaigns matching none go into "Diğer". Declared before the export
  // callback so useCallback can safely close over it without TDZ.
  const brandBreakdown = useMemo(() => {
    const campaigns = data?.campaigns || [];
    const mk = () => ({
      google: 0, meta: 0, total: 0,
      googleCost: 0, metaCost: 0, cost: 0,
      conversions: 0, revenue: 0,
      bqQty: 0, bqRevenue: 0,
      campaigns: 0,
    });
    const rows = BRAND_GROUPS.map((b) => ({ key: b.key, label: b.label, ...mk() }));
    const other = { key: 'OTHER', label: 'Diğer', ...mk() };
    const totals = mk();

    for (const c of campaigns) {
      const budget = Number(c.effectiveDailyBudget) || 0;
      const cost = Number(c.cost) || 0;
      const conv = Number(c.conversions) || 0;
      const rev = Number(c.revenue) || 0;
      const isGoogle = c.platform === 'GOOGLE_ADS';
      const isMeta = c.platform === 'META';
      let matched = null;
      for (const b of BRAND_GROUPS) {
        if (matchBrand(c.name, b.match || b.key)) { matched = b.key; break; }
      }
      const bucket = matched
        ? rows.find((r) => r.key === matched)
        : other;
      if (isGoogle) { bucket.google += budget; bucket.googleCost += cost; }
      if (isMeta) { bucket.meta += budget; bucket.metaCost += cost; }
      bucket.total += budget;
      bucket.cost += cost;
      bucket.conversions += conv;
      bucket.revenue += rev;
      bucket.campaigns += 1;
      if (isGoogle) { totals.google += budget; totals.googleCost += cost; }
      if (isMeta) { totals.meta += budget; totals.metaCost += cost; }
      totals.total += budget;
      totals.cost += cost;
      totals.conversions += conv;
      totals.revenue += rev;
      totals.campaigns += 1;
    }

    if (bqSales) {
      for (const row of rows) {
        const bq = bqSales[row.key];
        if (bq) {
          row.bqQty = bq.qty;
          row.bqRevenue = bq.revenue;
          totals.bqQty += bq.qty;
          totals.bqRevenue += bq.revenue;
        }
      }
    }

    return { rows, other, totals };
  }, [data, bqSales]);

  // --- Excel export -------------------------------------------------------
  // Builds a two-sheet workbook:
  //   1. Özet        — brand, date, filters + Google/Meta/Total budget summary
  //   2. Marka Bazlı — per-brand × platform daily budget split
  // Number columns use a Turkish currency format so Excel treats them as
  // real numbers (pivotable, summable) rather than strings.
  const exportToExcel = useCallback(() => {
    if (!data?.campaigns?.length) return;

    const google = (data.campaigns || []).filter((c) => c.platform === 'GOOGLE_ADS');
    const meta = (data.campaigns || []).filter((c) => c.platform === 'META');

    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const nowStr = `${todayStr} ${pad(today.getHours())}:${pad(today.getMinutes())}`;

    const wb = XLSX.utils.book_new();
    const MONEY_FMT = '#,##0.00 "₺";-#,##0.00 "₺";"—"';
    const INT_FMT = '#,##0;-#,##0;"—"';
    const PCT_FMT = '0.0%;-0.0%;"—"';

    const safeShare = (num, denom) => {
      const d = Number(denom) || 0;
      if (d <= 0) return 0;
      return (Number(num) || 0) / d;
    };

    const applyColFormat = (ws, colLetter, rowStart, rowEnd, fmt) => {
      for (let r = rowStart; r <= rowEnd; r++) {
        const addr = `${colLetter}${r}`;
        if (ws[addr] && ws[addr].v != null && ws[addr].v !== '') ws[addr].z = fmt;
      }
    };
    const setCols = (ws, widths) => {
      ws['!cols'] = widths.map((w) => ({ wch: w }));
    };
    const freezeHeader = (ws, rowsFromTop = 1) => {
      ws['!freeze'] = { xSplit: 0, ySplit: rowsFromTop };
      // SheetJS community build honors !freeze in some readers; also set the
      // more-portable !views for most consumers.
      ws['!views'] = [{ ySplit: rowsFromTop }];
    };

    const sum = (arr, k) => arr.reduce((s, c) => s + (Number(c[k]) || 0), 0);
    const totalGoogleBudget = sum(google, 'effectiveDailyBudget');
    const totalMetaBudget = sum(meta, 'effectiveDailyBudget');
    const totalGoogleCost = sum(google, 'cost');
    const totalMetaCost = sum(meta, 'cost');
    const totalGoogleConv = sum(google, 'conversions');
    const totalMetaConv = sum(meta, 'conversions');
    const totalGoogleRev = sum(google, 'revenue');
    const totalMetaRev = sum(meta, 'revenue');
    const dateLabel = describeRange(dateRange, customStart, customEnd);

    // ------ Sheet 1: Özet ------
    // Structure:
    //   • Title row (merged)
    //   • Metadata block (brand, date, filters)
    //   • Platform totals table (Google / Meta / Toplam)
    //   • Google Ads kampanya listesi (name · status · daily budget)
    //   • Meta Ads kampanya listesi (name · status · daily budget)
    // Row indexes are computed dynamically so every block stays contiguous.
    const summaryRows = [];
    summaryRows.push(['Birleşik Kampanya Raporu']);
    summaryRows.push([]);
    summaryRows.push(['Marka', selectedBrand?.name || selectedBrand?.id || '—']);
    summaryRows.push(['Rapor tarihi', nowStr]);
    summaryRows.push(['Durum filtresi', status === 'ENABLED' ? 'Aktif' : status === 'PAUSED' ? 'Duraklatıldı' : 'Hepsi']);
    summaryRows.push(['Platform filtresi', platform === 'ALL' ? 'Hepsi' : platform === 'GOOGLE_ADS' ? 'Google Ads' : 'Meta Ads']);
    summaryRows.push(['Harcama aralığı', dateLabel + (apiStartDate && apiEndDate ? `  (${apiStartDate} → ${apiEndDate})` : '')]);
    summaryRows.push([]);

    const grandBudget = totalGoogleBudget + totalMetaBudget;
    const grandCost = totalGoogleCost + totalMetaCost;

    // Platform totals block — budget, cost, conversions, revenue, ROAS, % share
    summaryRows.push(['Platform', 'Kampanya Sayısı', 'Günlük Bütçe', 'Harcama', 'Conv.', 'Revenue', 'ROAS', '% Pay']);
    const totalsHeaderRow = summaryRows.length; // 1-based row index of header
    summaryRows.push([
      'Google Ads', google.length, totalGoogleBudget, totalGoogleCost,
      totalGoogleConv, totalGoogleRev,
      totalGoogleCost > 0 ? totalGoogleRev / totalGoogleCost : 0,
      safeShare(totalGoogleBudget, grandBudget),
    ]);
    summaryRows.push([
      'Meta Ads', meta.length, totalMetaBudget, totalMetaCost,
      totalMetaConv, totalMetaRev,
      totalMetaCost > 0 ? totalMetaRev / totalMetaCost : 0,
      safeShare(totalMetaBudget, grandBudget),
    ]);
    summaryRows.push([
      'Toplam',
      google.length + meta.length,
      grandBudget,
      grandCost,
      totalGoogleConv + totalMetaConv,
      totalGoogleRev + totalMetaRev,
      grandCost > 0 ? (totalGoogleRev + totalMetaRev) / grandCost : 0,
      grandBudget > 0 ? 1 : 0,
    ]);
    const totalsLastRow = summaryRows.length; // inclusive, 1-based

    // Google Ads kampanya listesi — budget, cost, conv, revenue, ROAS, % pay
    let googleListHeaderRow = null;
    let googleListStart = null;
    let googleListEnd = null;
    if (google.length > 0) {
      summaryRows.push([]);
      summaryRows.push(['Google Ads Kampanyaları', '', '', '', '', '', '', '']);
      summaryRows.push(['Kampanya Adı', 'Durum', 'Günlük Bütçe', 'Harcama', 'Conv.', 'Revenue', 'ROAS', 'Toplamın Payı']);
      googleListHeaderRow = summaryRows.length;
      for (const c of google) {
        const b = Number(c.effectiveDailyBudget) || 0;
        const cost = Number(c.cost) || 0;
        const rev = Number(c.revenue) || 0;
        summaryRows.push([
          c.name || '',
          STATUS_LABEL[(c.status || '').toUpperCase()] || c.status || '',
          b, cost,
          Number(c.conversions) || 0,
          rev,
          cost > 0 ? rev / cost : 0,
          safeShare(b, grandBudget),
        ]);
      }
      googleListStart = googleListHeaderRow + 1;
      googleListEnd = summaryRows.length;
      summaryRows.push([
        'Ara toplam', '', totalGoogleBudget, totalGoogleCost,
        totalGoogleConv, totalGoogleRev,
        totalGoogleCost > 0 ? totalGoogleRev / totalGoogleCost : 0,
        safeShare(totalGoogleBudget, grandBudget),
      ]);
    }

    // Meta Ads kampanya listesi
    let metaListHeaderRow = null;
    let metaListStart = null;
    let metaListEnd = null;
    if (meta.length > 0) {
      summaryRows.push([]);
      summaryRows.push(['Meta Ads Kampanyaları', '', '', '', '', '', '', '']);
      summaryRows.push(['Kampanya Adı', 'Durum', 'Günlük Bütçe', 'Harcama', 'Conv.', 'Revenue', 'ROAS', 'Toplamın Payı']);
      metaListHeaderRow = summaryRows.length;
      for (const c of meta) {
        const b = Number(c.effectiveDailyBudget) || 0;
        const cost = Number(c.cost) || 0;
        const rev = Number(c.revenue) || 0;
        summaryRows.push([
          c.name || '',
          STATUS_LABEL[(c.status || '').toUpperCase()] || c.status || '',
          b, cost,
          Number(c.conversions) || 0,
          rev,
          cost > 0 ? rev / cost : 0,
          safeShare(b, grandBudget),
        ]);
      }
      metaListStart = metaListHeaderRow + 1;
      metaListEnd = summaryRows.length;
      summaryRows.push([
        'Ara toplam', '', totalMetaBudget, totalMetaCost,
        totalMetaConv, totalMetaRev,
        totalMetaCost > 0 ? totalMetaRev / totalMetaCost : 0,
        safeShare(totalMetaBudget, grandBudget),
      ]);
    }

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];
    setCols(wsSummary, [46, 20, 20, 20, 12, 20, 10, 14]);

    const ROAS_FMT = '0.00"x";-0.00"x";"—"';

    // Platform totals block: B=int, C-D=money, E=int (conv), F=money (rev), G=ROAS, H=pct
    applyColFormat(wsSummary, 'B', totalsHeaderRow + 1, totalsLastRow, INT_FMT);
    applyColFormat(wsSummary, 'C', totalsHeaderRow + 1, totalsLastRow, MONEY_FMT);
    applyColFormat(wsSummary, 'D', totalsHeaderRow + 1, totalsLastRow, MONEY_FMT);
    applyColFormat(wsSummary, 'E', totalsHeaderRow + 1, totalsLastRow, INT_FMT);
    applyColFormat(wsSummary, 'F', totalsHeaderRow + 1, totalsLastRow, MONEY_FMT);
    applyColFormat(wsSummary, 'G', totalsHeaderRow + 1, totalsLastRow, ROAS_FMT);
    applyColFormat(wsSummary, 'H', totalsHeaderRow + 1, totalsLastRow, PCT_FMT);

    const applyListFmt = (startRow, endRow) => {
      applyColFormat(wsSummary, 'C', startRow, endRow + 1, MONEY_FMT);
      applyColFormat(wsSummary, 'D', startRow, endRow + 1, MONEY_FMT);
      applyColFormat(wsSummary, 'E', startRow, endRow + 1, INT_FMT);
      applyColFormat(wsSummary, 'F', startRow, endRow + 1, MONEY_FMT);
      applyColFormat(wsSummary, 'G', startRow, endRow + 1, ROAS_FMT);
      applyColFormat(wsSummary, 'H', startRow, endRow + 1, PCT_FMT);
    };
    if (googleListStart) applyListFmt(googleListStart, googleListEnd);
    if (metaListStart) applyListFmt(metaListStart, metaListEnd);

    XLSX.utils.book_append_sheet(wb, wsSummary, 'Özet');

    // ------ Sheet 2: Marka Bazlı Performans ------
    {
      const headers = [
        'Marka',
        'Bütçe Google', 'Bütçe Meta', 'Bütçe Toplam',
        'Harcama',
        'BQ Ciro', 'ROAS',
        '% Pay', 'Kampanya Sayısı',
      ];
      const { rows, other, totals } = brandBreakdown;
      const grand = Number(totals.total) || 0;
      const makeRow = (r, label = r.label) => {
        const bqRev = Number(r.bqRevenue) || 0;
        return [
          label,
          r.google, r.meta, r.total,
          r.cost,
          bqRev,
          r.cost > 0 ? bqRev / r.cost : 0,
          safeShare(r.total, grand),
          r.campaigns,
        ];
      };
      const bodyRows = rows.map((r) => makeRow(r));
      if (other.campaigns > 0) {
        bodyRows.push(makeRow(other, 'Diğer (eşleşmeyen)'));
      }
      bodyRows.push([
        'TOPLAM',
        totals.google, totals.meta, totals.total,
        totals.cost,
        totals.bqRevenue,
        totals.cost > 0 ? totals.bqRevenue / totals.cost : 0,
        grand > 0 ? 1 : 0,
        totals.campaigns,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...bodyRows]);
      setCols(ws, [28, 18, 18, 18, 18, 18, 10, 10, 14]);

      const ROAS_FMT = '0.00"x";-0.00"x";"—"';
      ['B', 'C', 'D', 'E', 'F'].forEach((col) =>
        applyColFormat(ws, col, 2, bodyRows.length + 1, MONEY_FMT)
      );
      applyColFormat(ws, 'G', 2, bodyRows.length + 1, ROAS_FMT);
      applyColFormat(ws, 'H', 2, bodyRows.length + 1, PCT_FMT);
      applyColFormat(ws, 'I', 2, bodyRows.length + 1, INT_FMT);
      freezeHeader(ws, 1);
      XLSX.utils.book_append_sheet(wb, ws, 'Marka Bazlı');
    }

    const brandSlug = (selectedBrand?.name || 'brand')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    XLSX.writeFile(wb, `birlesik-kampanyalar-${brandSlug}-${todayStr}.xlsx`);
  }, [data, selectedBrand, status, platform, brandBreakdown]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    if (!data?.campaigns) return;
    setExpanded(new Set(data.campaigns
      .filter((c) => (c.children || []).length > 0)
      .map((c) => `${c.platform}:${c.id}`)));
  };
  const collapseAll = () => setExpanded(new Set());

  const summary = useMemo(() => {
    const init = {
      total: 0, google: 0, meta: 0, dailyBudget: 0, adsetBased: 0,
      googleBudget: 0, metaBudget: 0,
      googleCost: 0, metaCost: 0, totalCost: 0,
    };
    if (!data?.campaigns) return init;
    const t = data.campaigns.reduce((acc, c) => {
      acc.total += 1;
      const budget = Number(c.effectiveDailyBudget) || 0;
      const cost = Number(c.cost) || 0;
      if (c.platform === 'GOOGLE_ADS') { acc.google += 1; acc.googleBudget += budget; acc.googleCost += cost; }
      if (c.platform === 'META') { acc.meta += 1; acc.metaBudget += budget; acc.metaCost += cost; }
      acc.dailyBudget += budget;
      acc.totalCost += cost;
      if (c.budgetSource === 'adset_sum') acc.adsetBased += 1;
      return acc;
    }, init);
    return t;
  }, [data]);

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70 min-h-screen">
      {/* Header */}
      <div className="flex flex-wrap gap-4 justify-between items-center bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Birleşik Kampanyalar</h1>
            <p className="text-sm text-gray-500 mt-1">
              Google Ads + Meta Ads — günlük bütçe ve alt gruplar birlikte
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter
            value={dateRange}
            customStart={customStart}
            customEnd={customEnd}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStart(start);
              setCustomEnd(end);
            }}
            presets={['today', 'yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
          />
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="text-sm px-3 py-2 border border-gray-200 rounded-xl bg-white shadow-sm"
          >
            <option value="ALL">Tüm Platformlar</option>
            <option value="GOOGLE_ADS">Google Ads</option>
            <option value="META">Meta Ads</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="text-sm px-3 py-2 border border-gray-200 rounded-xl bg-white shadow-sm"
          >
            <option value="ALL">Hepsi (önerilen)</option>
            <option value="ENABLED">Aktif</option>
            <option value="PAUSED">Duraklatıldı</option>
          </select>
          <button
            onClick={() => fetchData({ force: true })}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </button>
          <button
            onClick={exportToExcel}
            disabled={loading || !data?.campaigns?.length}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm disabled:bg-emerald-300 disabled:cursor-not-allowed"
            title="Excel olarak indir"
          >
            <Download className="w-4 h-4" />
            Excel İndir
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Layers className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Toplam Kampanya</p>
              <p className="text-xl font-semibold">{summary.total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center">
              <span className="text-yellow-600 font-bold text-sm">G</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Google Ads</p>
              <p className="text-xl font-semibold">{summary.google}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fmtMoney(summary.googleBudget)} · <span className="text-yellow-700 font-medium">{fmtPct(summary.googleBudget, summary.dailyBudget)}</span>
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <span className="text-blue-600 font-bold text-sm">M</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Meta Ads</p>
              <p className="text-xl font-semibold">{summary.meta}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fmtMoney(summary.metaBudget)} · <span className="text-blue-700 font-medium">{fmtPct(summary.metaBudget, summary.dailyBudget)}</span>
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Günlük Bütçe Toplam</p>
              <p className="text-xl font-semibold">{fmtMoney(summary.dailyBudget)}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Harcama: <span className="text-emerald-700 font-medium">{fmtMoney(summary.totalCost)}</span>
                {' '}<span className="text-gray-400">({describeRange(dateRange, customStart, customEnd)})</span>
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Connection errors */}
      {data?.errors && (data.errors.google || data.errors.meta) && (
        <div className="flex flex-col gap-2">
          {data.errors.google && (
            <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span><b>Google Ads:</b> {data.errors.google}</span>
            </div>
          )}
          {data.errors.meta && (
            <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span><b>Meta Ads:</b> {data.errors.meta}</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Platform sections — Google and Meta render in separate cards */}
      {(platform === 'ALL' || platform === 'GOOGLE_ADS') && (
        <PlatformSection
          platformKey="GOOGLE_ADS"
          loading={loading}
          campaigns={(data?.campaigns || []).filter((c) => c.platform === 'GOOGLE_ADS')}
          expanded={expanded}
          onToggle={toggle}
          onExpandAll={() => {
            if (!data?.campaigns) return;
            setExpanded((prev) => {
              const next = new Set(prev);
              data.campaigns
                .filter((c) => c.platform === 'GOOGLE_ADS' && (c.children || []).length > 0)
                .forEach((c) => next.add(`${c.platform}:${c.id}`));
              return next;
            });
          }}
          onCollapseAll={() => {
            setExpanded((prev) => {
              const next = new Set(prev);
              for (const k of Array.from(next)) if (k.startsWith('GOOGLE_ADS:')) next.delete(k);
              return next;
            });
          }}
          cached={data?.cached}
          dateRangeLabel={describeRange(dateRange, customStart, customEnd)}
        />
      )}

      {(platform === 'ALL' || platform === 'META') && (
        <PlatformSection
          platformKey="META"
          loading={loading}
          campaigns={(data?.campaigns || []).filter((c) => c.platform === 'META')}
          expanded={expanded}
          onToggle={toggle}
          onExpandAll={() => {
            if (!data?.campaigns) return;
            setExpanded((prev) => {
              const next = new Set(prev);
              data.campaigns
                .filter((c) => c.platform === 'META' && (c.children || []).length > 0)
                .forEach((c) => next.add(`${c.platform}:${c.id}`));
              return next;
            });
          }}
          onCollapseAll={() => {
            setExpanded((prev) => {
              const next = new Set(prev);
              for (const k of Array.from(next)) if (k.startsWith('META:')) next.delete(k);
              return next;
            });
          }}
          cached={data?.cached}
          dateRangeLabel={describeRange(dateRange, customStart, customEnd)}
        />
      )}

      {/* Brand breakdown — always visible, reflects the currently-visible
          platform filter's campaign list. */}
      <BrandBreakdownCard
        breakdown={brandBreakdown}
        loading={loading}
        dateRangeLabel={describeRange(dateRange, customStart, customEnd)}
      />

      {/* Brand × week matrix: cost / BQ revenue / ROAS side by side, so a
          brand's weekly ROAS trend is readable across the row. Feeds off the
          same two weekly datasets as the pivots below — no extra request. */}
      <BrandWeeklyRoasMatrixCard
        weeks={weeklyByBrand}
        revenue={weeklyBQRevenue}
        loading={weeklyByBrandLoading || weeklyBQRevenueLoading}
        months={monthlyByBrand}
        monthRevenue={monthlyBQRevenue}
        monthLoading={monthlyLoading}
        onNeedMonthly={ensureMonthly}
        onFetchRange={fetchRangeRoas}
        brandId={selectedBrand?.id}
        brandGroups={BRAND_GROUPS}
      />

      {/* Aynı matrisin GÜN kırılımı — haftalık kartın hemen altında, son 30 gün
          penceresinden seçilen gün sayısı kadar kolon. */}
      <BrandDailyRoasMatrixCard
        days={dailyByBrand}
        revenue={dailyBQRevenue}
        loading={dailyByBrandLoading || dailyBQRevenueLoading}
        onRefresh={() => { fetchDailyByBrand({ force: true }); fetchDailyBQRevenue({ force: true }); }}
        brandGroups={BRAND_GROUPS}
      />

      {/* Weekly pivot for last 120 days — fixed historical window,
          independent of the page filters. */}
      <BrandWeeklyPivotCard
        weeks={weeklyByBrand}
        loading={weeklyByBrandLoading}
        onRefresh={() => fetchWeeklyByBrand({ force: true })}
        brandGroups={BRAND_GROUPS}
      />

      {/* Weekly BQ revenue pivot (item_brand × week) — last 120 days. */}
      <BrandWeeklyRevenuePivotCard
        data={weeklyBQRevenue}
        loading={weeklyBQRevenueLoading}
        onRefresh={() => fetchWeeklyBQRevenue({ force: true })}
      />

      {/* Unmatched (brand-less) ad campaigns with their GA4 revenue, for the
          selected date range — the campaign-level breakdown behind the spend
          pivot's "Diğer" bucket. */}
      <UnmatchedCampaignsCard
        data={ga4Campaigns}
        loading={ga4CampaignsLoading}
        onRefresh={() => fetchGa4Campaigns({ force: true })}
        brandGroups={BRAND_GROUPS}
        dateRangeLabel={describeRange(dateRange, customStart, customEnd)}
      />

      {/* Aynı matris, ciro yerine ERP'den yüklenen "Gerçekleşen Net Satış" ile.
          Sadece High5 — net satış Excel'ini yalnızca bu marka gönderiyor. */}
      {selectedBrand?.id === HIGH5_BRAND_ID && (
        <BrandWeeklyNetSalesMatrixCard
          weeks={weeklyByBrand}
          netSales={weeklyNetSales}
          loading={weeklyByBrandLoading || weeklyNetSalesLoading}
          onRefresh={() => fetchWeeklyNetSales({ force: true })}
          brandGroups={BRAND_GROUPS}
          brandId={selectedBrand?.id}
        />
      )}

      {/* Aynı verinin grafikleri — tablo yerine değil, tablonun altında. */}
      {selectedBrand?.id === HIGH5_BRAND_ID && (
        <BrandWeeklyNetSalesChartsCard
          weeks={weeklyByBrand}
          netSales={weeklyNetSales}
          loading={weeklyByBrandLoading || weeklyNetSalesLoading}
          brandGroups={BRAND_GROUPS}
          brandId={selectedBrand?.id}
        />
      )}

      {/* Net satış tablosundan otomatik üretilen haftalık yorum — yalnız heyglowy.com. */}
      {selectedBrand?.id === HIGH5_BRAND_ID && isHeyglowyUser(user) && (
        <BrandWeeklyNetSalesCommentaryCard
          weeks={weeklyByBrand}
          netSales={weeklyNetSales}
          loading={weeklyByBrandLoading || weeklyNetSalesLoading}
          brandGroups={BRAND_GROUPS}
        />
      )}

      {/* Aylık hedef bütçe dağılımı vs gerçekleşen. Planı olmayan markada
          bileşen kendi kendini gizler (hasPlan=false). */}
      <BudgetPlanCard
        data={budgetPlan}
        loading={budgetPlanLoading}
        onRefresh={() => fetchBudgetPlan()}
        onChangeMonth={(y, m) => fetchBudgetPlan({ year: y, month: m })}
        brandGroups={BRAND_GROUPS}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brand × Week pivot, last 120 days. Cells show ₺ value + share of week total.
// ---------------------------------------------------------------------------
// Haftalık satırları ({week, brands{}, total}) aya çevirir. "month" = haftanın
// pazartesisinin YYYY-MM'i; ay sınırını kesen hafta komple pazartesisinin ayına
// yazılır — YAKLAŞIKTIR.
//
// DİKKAT: BQ Ciro kartı artık bunu kullanmıyor. Ölçüldü, sapma "≤%1-2 edge-bleed"
// değil: High5'te aylık ciro /analytics/bq-item-sales'e göre %-8,9 ile %+17,4
// arasında kayıyordu. BQ ciro için ay artık backend'den takvim ayı olarak
// geliyor (weekly-bq-revenue → monthlyRows); bu fonksiyon yalnızca
//   • harcama pivotu (weekly-live'ın gün kırılımı yok, canlı API haftalık geliyor)
//   • gün kırılımı olmayan markaların BQ cirosu (monthGrain === 'week-bucket')
// için kaldı ve o kartlarda başlıkta "yaklaşık" olarak işaretleniyor.
function rebucketByMonth(rows) {
  if (!Array.isArray(rows)) return [];
  const byMonth = new Map();
  for (const r of rows) {
    if (!r?.week) continue;
    const key = String(r.week).slice(0, 7); // YYYY-MM
    if (!byMonth.has(key)) byMonth.set(key, { period: key, brands: {}, total: 0 });
    const m = byMonth.get(key);
    for (const [bk, bv] of Object.entries(r.brands || {})) {
      m.brands[bk] = (m.brands[bk] || 0) + (Number(bv) || 0);
    }
    m.total += Number(r.total) || 0;
  }
  return [...byMonth.values()].sort((a, b) => b.period.localeCompare(a.period));
}

const TR_MONTHS = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

// Bir önceki döneme (hafta/ay) göre yüzde değişim rozeti. Artış yeşil ▲, düşüş
// kırmızı ▼. Önceki dönemde veri yoksa "yeni", bu dönemde değer yoksa hiç gösterme.
function PeriodDelta({ cur, prev }) {
  if (!(cur > 0)) return null;          // bu dönemde değer yok → rozet yok
  if (prev == null) return null;        // önceki dönem yok (en eski satır) → rozet yok
  if (!(prev > 0)) {
    return <div className="text-[10px] font-semibold text-emerald-600" title="önceki dönemde veri yoktu">▲ yeni</div>;
  }
  const pct = (cur - prev) / prev * 100;
  const r = Math.round(pct);
  if (r === 0) return <div className="text-[10px] text-gray-400">≈%0</div>;
  const up = r > 0;
  return (
    <div
      className={`text-[10px] font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}
      title={`önceki döneme göre %${up ? '+' : '−'}${Math.abs(r)}`}
    >
      {up ? '▲' : '▼'} %{Math.abs(r)}
    </div>
  );
}

// opts verilmezse Hafta/Ay — ROAS matrisi üçüncü seçenek (Tarih) için kendi
// listesini geçiyor, diğer iki pivot kartı olduğu gibi kalsın.
function PeriodToggle({ value, onChange, opts: customOpts }) {
  const opts = customOpts || [
    { v: 'week', label: 'Hafta' },
    { v: 'month', label: 'Ay' }
  ];
  return (
    <div className="inline-flex rounded-xl border border-gray-200 bg-white p-0.5 shadow-sm">
      {opts.map(o => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            value === o.v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function BrandWeeklyPivotCard({ weeks, loading, onRefresh, brandGroups }) {
  const BRAND_GROUPS = brandGroups || [];
  const [period, setPeriod] = useState('week');
  const fmtK = (v) => {
    if (v == null || v === 0) return '—';
    if (v >= 1_000_000) return `₺${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
    if (v >= 1_000) return `₺${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}K`;
    return `₺${Math.round(v).toLocaleString('tr-TR')}`;
  };
  const fmtRange = (weekStart) => {
    const s = new Date(weekStart + 'T00:00:00');
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    return { range: `${f(s)} – ${f(e)}`, year: e.getFullYear() };
  };
  const fmtMonth = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return { range: TR_MONTHS[(m || 1) - 1], year: y };
  };
  const cols = [...BRAND_GROUPS.map(b => b.key), 'Other'];

  const displayRows = useMemo(() => {
    if (period === 'month') {
      return rebucketByMonth(weeks).map(r => ({ key: r.period, label: fmtMonth(r.period), brands: r.brands, total: r.total }));
    }
    return (weeks || []).map(w => ({ key: w.week, label: fmtRange(w.week), brands: w.brands, total: w.total || 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, period]);

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-amber-200 bg-amber-50 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800">Marka Bazlı {period === 'month' ? 'Aylık' : 'Haftalık'} Performans</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Live · Son 120 gün · {period === 'month' ? 'Aylık YAKLAŞIK (harcama canlı API\'den haftalık geliyor, hafta pazartesisine göre)' : 'Hafta başı Pazartesi'} · Hücreler harcama + dönem payı
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PeriodToggle value={period} onChange={setPeriod} />
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
                title="Live veriyi yeniden çek"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[560px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-3 text-left whitespace-nowrap">{period === 'month' ? 'Ay' : 'Hafta'}</th>
              {cols.map(c => (
                <th key={c} className="px-4 py-3 text-right whitespace-nowrap">{c}</th>
              ))}
              <th className="px-4 py-3 text-right whitespace-nowrap border-l border-gray-200">Toplam</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={cols.length + 2} className="py-8 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!loading && displayRows.length === 0 && (
              <tr><td colSpan={cols.length + 2} className="py-8 text-center text-gray-400">Veri yok</td></tr>
            )}
            {!loading && displayRows.map((row, ri) => {
              const wkTotal = row.total || 0;
              const prevRow = displayRows[ri + 1]; // bir önceki (daha eski) dönem
              return (
                <tr key={row.key} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                    {row.label.range} <span className="text-gray-400 font-normal">{row.label.year}</span>
                  </td>
                  {cols.map(c => {
                    const val = row.brands[c] || 0;
                    const pct = wkTotal > 0 && val > 0 ? (val / wkTotal * 100).toFixed(1) + '%' : null;
                    const prevVal = prevRow ? (prevRow.brands[c] || 0) : null;
                    return (
                      <td key={c} className="px-4 py-3 text-right tabular-nums text-gray-700">
                        {val > 0 ? (
                          <div>
                            <div>{fmtK(val)}</div>
                            <div className="text-xs text-gray-400">{pct}</div>
                            <PeriodDelta cur={val} prev={prevVal} />
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900 border-l border-gray-100">
                    <div>{fmtK(wkTotal)}</div>
                    <PeriodDelta cur={wkTotal} prev={prevRow ? (prevRow.total || 0) : null} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Brand × Week BQ revenue pivot, last 120 days. Source: bq_ga4_item_sales_daily.
// Brand columns are dynamic (driven by item_brand values), ordered by total
// revenue desc. No spend column.
// ---------------------------------------------------------------------------
function BrandWeeklyRevenuePivotCard({ data, loading, onRefresh }) {
  const [period, setPeriod] = useState('week');
  const fmtK = (v) => {
    if (v == null || v === 0) return '—';
    if (v >= 1_000_000) return `₺${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
    if (v >= 1_000) return `₺${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}K`;
    return `₺${Math.round(v).toLocaleString('tr-TR')}`;
  };
  const fmtRange = (weekStart) => {
    const s = new Date(weekStart + 'T00:00:00');
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    return { range: `${f(s)} – ${f(e)}`, year: e.getFullYear() };
  };
  const fmtMonth = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return { range: TR_MONTHS[(m || 1) - 1], year: y };
  };
  const brands = data?.brands || [];
  // Backend takvim ayını gün satırlarından kuruyor. Gün kırılımı olmayan
  // markalarda (yalnızca haftalık view var) monthGrain 'week-bucket' döner ve
  // ay yaklaşık kalır — başlıkta belirtiliyor.
  const monthApprox = data?.monthGrain === 'week-bucket';

  const displayRows = useMemo(() => {
    const rows = data?.rows || [];
    if (period === 'month') {
      const monthly = data?.monthlyRows;
      if (Array.isArray(monthly)) {
        return monthly.map(m => ({ key: m.month, label: fmtMonth(m.month), brands: m.brands || {}, total: m.total || 0 }));
      }
      // Eski payload (cache'te kalmış yanıt) → haftadan türet.
      return rebucketByMonth(rows).map(r => ({ key: r.period, label: fmtMonth(r.period), brands: r.brands, total: r.total }));
    }
    return rows.map(w => ({ key: w.week, label: fmtRange(w.week), brands: w.brands || {}, total: w.total || 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, period]);

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800">Marka Bazlı {period === 'month' ? 'Aylık' : 'Haftalık'} BQ Ciro</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {period === 'month'
                  ? (monthApprox
                      ? 'Son 120 gün · Aylık YAKLAŞIK (bu markada gün kırılımı yok, hafta pazartesisine göre)'
                      : 'Takvim ayı · pencereye giren aylar tam, içinde bulunulan ay bugüne kadar')
                  : 'Son 120 gün · Hafta başı Pazartesi'}
                {' · GA4 item_sales · Markalar item_brand\'den otomatik'}
              </p>
              {period === 'month' && !monthApprox && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  /analytics/bq-item-sales ile aynı tablo ve aynı ay tanımı — rakamlar birebir tutmalı
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PeriodToggle value={period} onChange={setPeriod} />
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
                title="Veriyi yeniden çek"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[560px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-3 text-left whitespace-nowrap">{period === 'month' ? 'Ay' : 'Hafta'}</th>
              {brands.map(b => (
                <th key={b} className="px-4 py-3 text-right whitespace-nowrap">{b}</th>
              ))}
              <th className="px-4 py-3 text-right whitespace-nowrap border-l border-gray-200">Toplam</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={brands.length + 2} className="py-8 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!loading && displayRows.length === 0 && (
              <tr><td colSpan={(brands.length || 1) + 1} className="py-8 text-center text-gray-400">Veri yok</td></tr>
            )}
            {!loading && displayRows.map((row, ri) => {
              const wkTotal = row.total || 0;
              const prevRow = displayRows[ri + 1]; // bir önceki (daha eski) dönem
              return (
                <tr key={row.key} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                    {row.label.range} <span className="text-gray-400 font-normal">{row.label.year}</span>
                  </td>
                  {brands.map(b => {
                    const val = row.brands?.[b] || 0;
                    const pct = wkTotal > 0 && val > 0 ? (val / wkTotal * 100).toFixed(1) + '%' : null;
                    const prevVal = prevRow ? (prevRow.brands?.[b] || 0) : null;
                    return (
                      <td key={b} className="px-4 py-3 text-right tabular-nums text-gray-700">
                        {val > 0 ? (
                          <div>
                            <div>{fmtK(val)}</div>
                            <div className="text-xs text-gray-400">{pct}</div>
                            <PeriodDelta cur={val} prev={prevVal} />
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900 border-l border-gray-100">
                    <div>{fmtK(wkTotal)}</div>
                    <PeriodDelta cur={wkTotal} prev={prevRow ? (prevRow.total || 0) : null} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Brand × Week ROAS matrix. Rows = brand groups, columns = weeks, each week
// carrying three sub-columns: Harcama / BQ Ciro / ROAS. Reuses the two weekly
// datasets already fetched for the pivots above (weekly-live cost +
// weekly-bq-revenue), so it costs no extra request.
//
// Caveat, same as BrandBreakdownCard: cost is bucketed by campaign NAME while
// revenue is bucketed by GA4 item_brand. A brand whose campaign naming carries
// no keyword shows revenue with no cost (ROAS "—") and vice versa.
// ---------------------------------------------------------------------------
// Yerel gün aritmetiği — `new Date(iso)` UTC'de çözüp saat dilimine göre bir gün
// kaydırabiliyor, o yüzden tarihler string olarak kuruluyor.
const isoGun = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const gunEkle = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(y, m - 1, d);
  t.setDate(t.getDate() + n);
  return isoGun(t);
};
const gunFarki = (a, b) => {
  const [y1, m1, d1] = a.split('-').map(Number);
  const [y2, m2, d2] = b.split('-').map(Number);
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000);
};
const gunEtiket = (iso) => {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${(TR_MONTHS[m - 1] || '').slice(0, 3)}`;
};

function BrandWeeklyRoasMatrixCard({
  weeks, revenue, loading,
  months, monthRevenue, monthLoading, onNeedMonthly,
  onFetchRange, brandId,
  brandGroups,
}) {
  const BRAND_GROUPS = brandGroups || [];
  const [period, setPeriod] = useState('week');
  const [weekCount, setWeekCount] = useState(6);
  const [monthCount, setMonthCount] = useState(6);
  const isMonth = period === 'month';
  const isRange = period === 'range';
  const colCountSel = isMonth ? monthCount : weekCount;

  // ---- Tarih modu ---------------------------------------------------------
  // Varsayılan son 30 gün. Kıyas kolonu, seçilen aralığın HEMEN ÖNCESİNDEKİ eşit
  // uzunlukta dönem (30 günlük seçim → önceki 30 gün).
  const bugun = isoGun(new Date());
  const [basl, setBasl] = useState(gunEkle(bugun, -29));
  const [bit, setBit] = useState(bugun);
  const [rangeData, setRangeData] = useState(null);
  const [rangeBusy, setRangeBusy] = useState(false);
  const [rangeHata, setRangeHata] = useState(null);
  const [uygulanan, setUygulanan] = useState(null);   // {start,end,prevStart,prevEnd}

  const uygula = React.useCallback(async (start, end) => {
    if (!onFetchRange) return;
    if (!start || !end || start > end) { setRangeHata('Başlangıç bitişten sonra olamaz'); return; }
    const uzunluk = gunFarki(start, end) + 1;
    const prevEnd = gunEkle(start, -1);
    const prevStart = gunEkle(prevEnd, -(uzunluk - 1));
    setRangeBusy(true);
    setRangeHata(null);
    try {
      const d = await onFetchRange({ start, end, prevStart, prevEnd });
      setRangeData(d);
      setUygulanan({ start, end, prevStart, prevEnd, uzunluk });
      if (d?.failed) setRangeHata('Bazı veriler alınamadı — eksik görünebilir');
    } catch (e) {
      setRangeHata('Veri alınamadı');
    } finally {
      setRangeBusy(false);
    }
  }, [onFetchRange]);

  // 🔴 Marka değişince tarih verisi BAYAT kalır — hafta/ay props'tan tazeleniyor
  // ama tarih verisi kartın kendi state'inde. Temizlenmezse yeni markanın
  // başlığı altında eski markanın rakamları durur.
  useEffect(() => {
    setRangeData(null);
    setUygulanan(null);
    setRangeHata(null);
    // Kullanıcı zaten Tarih görünümündeyse boş tabloya düşmesin, aynı aralığı
    // yeni marka için tekrar çek.
    if (period === 'range') uygula(basl, bit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Ay ve tarih verisi tembel: sayfa açılışında çekilmiyor, ilk tıklamada geliyor.
  const switchPeriod = (p) => {
    if (p === 'month' && onNeedMonthly) onNeedMonthly();
    if (p === 'range' && !uygulanan && !rangeBusy) uygula(basl, bit);
    setPeriod(p);
  };

  // Ay ciro kırılımı: backend takvim ayını gün satırlarından kuruyor. Gün
  // kırılımı olmayan markada 'week-bucket' döner → ay YAKLAŞIK, başlıkta yazıyor.
  const monthApprox = isMonth && monthRevenue?.monthGrain === 'week-bucket';

  // Kolon sayısı yüksek (hafta × 3) → ₺ işareti başlıkta, hücrede sadece sayı.
  const fmtK = (v) => {
    if (!v) return '—';
    if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
    if (v >= 1_000) return `${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}K`;
    return Math.round(v).toLocaleString('tr-TR');
  };
  const fmtFull = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
  // Basamak sayısı değere göre — 12 haftada kolon 35px'e iniyor ve sabit 2 hane
  // ("62,45") hücreden taşıp karta yatay kaydırma çubuğu getiriyordu. Tam değer
  // tooltip'te; 62,45x ROAS'ta virgülden sonrası zaten bilgi taşımıyor.
  const fmtRoas = (v) => {
    if (v == null) return '—';
    if (v >= 1000) return '999+';
    const d = v >= 100 ? 0 : v >= 10 ? 1 : 2;
    return v.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
  };
  const roasTitle = (v) => (v == null ? '' : `ROAS ${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`);
  const roasCls = (v) => (v == null ? 'text-gray-300' : v >= 3 ? 'text-emerald-600' : v >= 1 ? 'text-amber-600' : 'text-red-500');
  const fmtRange = (weekStart) => {
    const s = new Date(weekStart + 'T00:00:00');
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    return { range: `${f(s)} – ${f(e)}`, year: e.getFullYear() };
  };
  // Ay başlığı: "Ağu 25" — kolon 3 alt sütuna bölündüğü için kısa ay + iki haneli yıl.
  const fmtMonthCol = (ym) => {
    const [y, m] = String(ym).split('-').map(Number);
    return { range: `${(TR_MONTHS[(m || 1) - 1] || '').slice(0, 3)} ${String(y).slice(2)}`, year: y };
  };
  // Tarih modunda kolon iki taneden ibaret: seçili aralık ve öncesindeki eşit dönem.
  const fmtRangeCol = (k) => {
    if (!uygulanan) return { range: '—' };
    const [s, e] = k === 'cur'
      ? [uygulanan.start, uygulanan.end]
      : [uygulanan.prevStart, uygulanan.prevEnd];
    return {
      range: `${gunEtiket(s)} – ${gunEtiket(e)}`,
      alt: k === 'cur' ? 'seçili aralık' : 'önceki eşit dönem',
    };
  };

  // Cost pivot is already keyed by BRAND_GROUPS keys; the revenue pivot is keyed
  // by raw item_brand strings, so remap it onto the same keys.
  const { weekList, matrix, totalsRow } = useMemo(() => {
    const keys = [...BRAND_GROUPS.map(b => b.key), 'Other'];
    // Hafta görünümünde anahtar 'week' + weekly-bq-revenue.rows, ay görünümünde
    // 'month' + monthlyRows (backend'in TAKVİM AYI pivotu). Ay harcaması da
    // segments.month / time_increment=monthly'den geliyor — iki taraf da gerçek
    // takvim ayı, hafta kovasından türetme YOK. Tarih görünümünde anahtar
    // 'period' ve yalnız iki değer var: seçili aralık + önceki eşit dönem.
    const keyField = isRange ? 'period' : isMonth ? 'month' : 'week';
    const costSrc = isRange ? (rangeData?.cost || []) : isMonth ? (months || []) : (weeks || []);
    const revSrc = isRange
      ? (rangeData?.revenue || [])
      : isMonth ? (monthRevenue?.monthlyRows || []) : (revenue?.rows || []);

    const cost = {};
    for (const w of costSrc) {
      const k = w?.[keyField];
      if (!k) continue;
      cost[k] = w.brands || {};
    }
    const rev = {};
    for (const w of revSrc) {
      const k = w?.[keyField];
      if (!k) continue;
      const bucket = {};
      keys.forEach(kk => { bucket[kk] = 0; });
      for (const [label, val] of Object.entries(w.brands || {})) {
        let matched = 'Other';
        for (const b of BRAND_GROUPS) {
          if (matchBrand(label, b.match || b.key)) { matched = b.key; break; }
        }
        bucket[matched] += Number(val) || 0;
      }
      rev[k] = bucket;
    }

    // Newest period first — same reading direction as the pivots above.
    // Tarih modunda alfabetik sıralama 'prev'i öne atardı; sıra ELLE veriliyor.
    const allWeeks = Array.from(new Set([...Object.keys(cost), ...Object.keys(rev)]));
    const shown = isRange
      ? ['cur', 'prev'].filter(k => allWeeks.includes(k))
      : allWeeks.sort((a, b) => b.localeCompare(a)).slice(0, colCountSel);

    const cell = (c, r) => {
      const co = Number(c) || 0;
      const re = Number(r) || 0;
      return { cost: co, revenue: re, roas: co > 0 ? re / co : null };
    };
    const rows = keys.map(k => ({
      key: k,
      label: k === 'Other' ? 'Diğer (eşleşmeyen)' : (BRAND_GROUPS.find(b => b.key === k)?.label || k),
      cells: shown.map(w => cell(cost[w]?.[k], rev[w]?.[k])),
    })).filter(r => r.cells.some(c => c.cost > 0 || c.revenue > 0));

    const totals = {
      key: '__total__',
      label: 'TOPLAM',
      cells: shown.map((_, i) => {
        const co = rows.reduce((s, r) => s + r.cells[i].cost, 0);
        const re = rows.reduce((s, r) => s + r.cells[i].revenue, 0);
        return { cost: co, revenue: re, roas: co > 0 ? re / co : null };
      }),
    };

    return { weekList: shown, matrix: rows, totalsRow: totals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, revenue, months, monthRevenue, rangeData, isMonth, isRange, brandGroups, colCountSel]);

  // ROAS trend arrow vs. the next column to the right (= the older period).
  const RoasDelta = ({ cur, prev }) => {
    if (cur == null || prev == null || !(prev > 0)) return null;
    const diff = cur - prev;
    if (Math.abs(diff) < 0.005) return <div className="text-[10px] text-gray-400">≈</div>;
    const up = diff > 0;
    return (
      <div
        className={`text-[10px] font-semibold whitespace-nowrap ${up ? 'text-emerald-600' : 'text-red-500'}`}
        title={`önceki ${isRange ? 'eşit döneme' : isMonth ? 'aya' : 'haftaya'} göre ${up ? '+' : '−'}${Math.abs(diff).toFixed(2)}x`}
      >
        {up ? '▲' : '▼'}{dense ? '' : ` ${Math.abs(diff).toFixed(2)}`}
      </div>
    );
  };

  // Tablo yatay kaydırmasız (table-fixed) olduğu için dönem sayısı sınırlı:
  // 12 kolonda 37 alt kolon oluşur, daha fazlası okunamaz hale gelir.
  const COUNT_OPTS = isMonth ? [3, 6, 12] : [4, 6, 8, 12];
  const colCount = weekList.length * 3 + 1;
  // 12 kolonda kolon ~35px'e iniyor: yazı/padding küçülür, ROAS trendi sadece ok
  // olarak basılır ("▼ 9.58" alt satıra taşıyordu) ve alt başlıklar kısalır.
  // Değerlerin tamamı yine tooltip'te.
  const dense = !isRange && colCountSel >= 12;
  const busy = isRange ? rangeBusy : isMonth ? monthLoading : loading;
  const numCls = dense ? 'px-0.5 text-[10px]' : 'px-1 text-xs';

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-emerald-200 bg-emerald-50 flex items-center justify-center">
              <Layers className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800">
                Marka Bazlı {isRange ? 'Seçili Tarih' : isMonth ? 'Aylık' : 'Haftalık'} Harcama · BQ Ciro · ROAS
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Satırlar marka, kolonlar {isRange ? 'seçili aralık + önceki eşit dönem' : isMonth ? 'takvim ayı' : 'hafta'} · Tutarlar ₺ (K = bin, M = milyon; tam değer için hücrenin üzerine gel) · Harcama live Google+Meta, ciro GA4 item_sales
              </p>
              {isRange && uygulanan && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {uygulanan.uzunluk} günlük aralık · kıyas kolonu hemen öncesindeki eşit dönem
                  {' ('}{gunEtiket(uygulanan.prevStart)} – {gunEtiket(uygulanan.prevEnd)}{')'}
                  {' · Harcama ve ciro AYNI tarih aralığından, dönem kırılımı yok'}
                </p>
              )}
              {isRange && rangeHata && (
                <p className="text-[11px] text-red-500 mt-0.5">{rangeHata}</p>
              )}
              {isMonth && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {monthApprox
                    ? 'Ay YAKLAŞIK: bu markada ciroda gün kırılımı yok, hafta pazartesisine göre toplanıyor'
                    : 'Harcama segments.month / time_increment=monthly, ciro takvim ayı — iki taraf da gerçek ay, hafta kovasından türetme yok'}
                  {' · Son 12 ay · İçinde bulunulan ay bugüne kadar'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <PeriodToggle
              value={period}
              onChange={switchPeriod}
              opts={[{ v: 'week', label: 'Hafta' }, { v: 'month', label: 'Ay' }, { v: 'range', label: 'Tarih' }]}
            />
            {isRange ? (
              <div className="inline-flex items-center gap-1.5">
                <input
                  type="date"
                  value={basl}
                  max={bit}
                  onChange={(e) => setBasl(e.target.value)}
                  className="px-2 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-700"
                />
                <span className="text-gray-400 text-xs">–</span>
                <input
                  type="date"
                  value={bit}
                  min={basl}
                  onChange={(e) => setBit(e.target.value)}
                  className="px-2 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-700"
                />
                <button
                  type="button"
                  onClick={() => uygula(basl, bit)}
                  disabled={rangeBusy}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                >
                  {rangeBusy ? 'Çekiliyor…' : 'Uygula'}
                </button>
              </div>
            ) : (
              <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
                {COUNT_OPTS.map(o => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => (isMonth ? setMonthCount(o) : setWeekCount(o))}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      colCountSel === o ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title={`Son ${o} ${isMonth ? 'ay' : 'hafta'}`}
                  >
                    {`${o}${isMonth ? 'a' : 'h'}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* table-fixed + w-full: kolonlar konteynere sığacak şekilde daralır,
          yatay kaydırma çubuğu çıkmaz. Dikey kırpma da YOK — marka listesi
          640px'e sığmadığında kart içinde kaydırılıyordu, tüm satırlar görünsün. */}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col style={{ width: dense ? '7.5rem' : '9rem' }} />
            {weekList.map(w => (
              <React.Fragment key={w}>
                <col /><col /><col />
              </React.Fragment>
            ))}
          </colgroup>
          <thead className="sticky top-0 bg-gray-50 z-20">
            <tr className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <th rowSpan={2} className="px-3 py-2 text-left border-r border-gray-200">
                Marka
              </th>
              {weekList.map(w => {
                const l = isRange ? fmtRangeCol(w) : isMonth ? fmtMonthCol(w) : fmtRange(w);
                return (
                  <th key={w} colSpan={3} className={`${dense ? 'px-0.5 text-[10px]' : 'px-1'} py-2 text-center border-l border-gray-200 leading-tight`}>
                    {l.range}
                    {l.alt && <div className="text-[9px] font-normal text-gray-400 normal-case">{l.alt}</div>}
                  </th>
                );
              })}
            </tr>
            <tr className={`font-medium text-gray-400 uppercase ${dense ? 'text-[9px] tracking-tight' : 'text-[10px] tracking-wide'}`}>
              {weekList.map(w => (
                <React.Fragment key={w}>
                  <th className={`${numCls} py-1.5 text-right border-l border-gray-200`} title="Harcama (₺)">{dense ? 'Har' : 'Harc.'}</th>
                  <th className={`${numCls} py-1.5 text-right`} title="BQ Ciro (₺)">{dense ? 'Cir' : 'Ciro'}</th>
                  <th className={`${numCls} py-1.5 text-right`} title="ROAS">ROAS</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr><td colSpan={colCount} className="py-8 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!busy && matrix.length === 0 && (
              <tr><td colSpan={colCount} className="py-8 text-center text-gray-400">Veri yok</td></tr>
            )}
            {!busy && matrix.map(row => (
              <tr key={row.key} className="border-t border-gray-100 hover:bg-gray-50/50">
                <td className="px-3 py-2 font-medium text-gray-800 text-xs truncate border-r border-gray-200" title={row.label}>
                  {row.label}
                </td>
                {row.cells.map((c, i) => (
                  <React.Fragment key={weekList[i]}>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-700 whitespace-nowrap border-l border-gray-200`} title={c.cost > 0 ? fmtFull(c.cost) : ''}>
                      {c.cost > 0 ? fmtK(c.cost) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-700 whitespace-nowrap`} title={c.revenue > 0 ? fmtFull(c.revenue) : ''}>
                      {c.revenue > 0 ? fmtK(c.revenue) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className={`${numCls} py-2 text-right tabular-nums font-semibold whitespace-nowrap ${roasCls(c.roas)}`} title={roasTitle(c.roas)}>
                      <div>{fmtRoas(c.roas)}</div>
                      <RoasDelta cur={c.roas} prev={row.cells[i + 1]?.roas ?? null} />
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
            {!busy && matrix.length > 0 && (
              <tr className="border-t-2 border-gray-300 bg-gray-50/70 font-semibold">
                <td className="px-3 py-2 text-xs text-gray-900 border-r border-gray-200">
                  {totalsRow.label}
                </td>
                {totalsRow.cells.map((c, i) => (
                  <React.Fragment key={weekList[i]}>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-900 whitespace-nowrap border-l border-gray-200`} title={fmtFull(c.cost)}>{fmtK(c.cost)}</td>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-900 whitespace-nowrap`} title={fmtFull(c.revenue)}>{fmtK(c.revenue)}</td>
                    <td className={`${numCls} py-2 text-right tabular-nums whitespace-nowrap ${roasCls(c.roas)}`} title={roasTitle(c.roas)}>
                      <div>{fmtRoas(c.roas)}</div>
                      <RoasDelta cur={c.roas} prev={totalsRow.cells[i + 1]?.roas ?? null} />
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BrandWeeklyRoasMatrixCard'ın GÜN kırılımlı ikizi: aynı üç kolon (harcama / BQ
// ciro / ROAS), hafta yerine gün. Harcama /daily-live (Google segments.date +
// Meta time_increment=1), ciro /daily-bq-revenue (bq_ga4_item_sales_daily).
//
// Neden ayrı istek: haftalık kart 120 günü hafta kovalarında çekiyor; aynı
// pencereyi günlüğe çevirmek hem Meta'da ~7× satır hem de anlamsız genişlikte
// bir tablo demek. Günlük pencere sabit 30 gün, tabloda 7/14/30 gösterilir.
// ---------------------------------------------------------------------------
function BrandDailyRoasMatrixCard({ days, revenue, loading, onRefresh, brandGroups }) {
  const BRAND_GROUPS = brandGroups || [];
  const [dayCount, setDayCount] = useState(7);

  const fmtK = (v) => {
    if (!v) return '—';
    if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
    if (v >= 1_000) return `${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}K`;
    return Math.round(v).toLocaleString('tr-TR');
  };
  const fmtFull = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
  // Basamak sayısı değere göre: kolon genişliği gün sayısıyla daralıyor ve sabit
  // 2 hane ("451,95") en dar modda hücreden taşıp yatay kaydırma çubuğu doğuruyordu.
  // Zaten 451,95x ROAS'ta virgülden sonrası bilgi taşımıyor; tam değer tooltip'te.
  const fmtRoas = (v) => {
    if (v == null) return '—';
    if (v >= 1000) return '999+';
    const d = v >= 100 ? 0 : v >= 10 ? 1 : 2;
    return v.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
  };
  const roasTitle = (v) => (v == null ? '' : `ROAS ${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`);
  const roasCls = (v) => (v == null ? 'text-gray-300' : v >= 3 ? 'text-emerald-600' : v >= 1 ? 'text-amber-600' : 'text-red-500');

  const todayKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  const dayLabel = (key) => {
    const d = new Date(key + 'T00:00:00');
    return {
      date: d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
      weekday: d.toLocaleDateString('tr-TR', { weekday: 'short' }),
      // getDay: 0=Paz, 6=Cmt
      weekend: d.getDay() === 0 || d.getDay() === 6,
    };
  };

  // Harcama pivotu zaten BRAND_GROUPS anahtarlarıyla geliyor; ciro pivotu ham
  // item_brand metniyle geliyor → aynı kovalara matchBrand ile eşlenir.
  const { dayList, matrix, totalsRow } = useMemo(() => {
    const keys = [...BRAND_GROUPS.map(b => b.key), 'Other'];
    const cost = {};
    for (const d of days || []) {
      if (!d?.date) continue;
      cost[d.date] = d.brands || {};
    }
    const rev = {};
    for (const d of revenue?.rows || []) {
      if (!d?.date) continue;
      const bucket = {};
      keys.forEach(k => { bucket[k] = 0; });
      for (const [label, val] of Object.entries(d.brands || {})) {
        let matched = 'Other';
        for (const b of BRAND_GROUPS) {
          if (matchBrand(label, b.match || b.key)) { matched = b.key; break; }
        }
        bucket[matched] += Number(val) || 0;
      }
      rev[d.date] = bucket;
    }

    // En yeni gün en solda — haftalık kartla aynı okuma yönü.
    const allDays = Array.from(new Set([...Object.keys(cost), ...Object.keys(rev)]))
      .sort((a, b) => b.localeCompare(a));
    const shown = allDays.slice(0, dayCount);

    const cell = (c, r) => {
      const co = Number(c) || 0;
      const re = Number(r) || 0;
      return { cost: co, revenue: re, roas: co > 0 ? re / co : null };
    };
    const rows = keys.map(k => ({
      key: k,
      label: k === 'Other' ? 'Diğer (eşleşmeyen)' : (BRAND_GROUPS.find(b => b.key === k)?.label || k),
      cells: shown.map(d => cell(cost[d]?.[k], rev[d]?.[k])),
    })).filter(r => r.cells.some(c => c.cost > 0 || c.revenue > 0));

    const totals = {
      key: '__total__',
      label: 'TOPLAM',
      cells: shown.map((_, i) => {
        const co = rows.reduce((s, r) => s + r.cells[i].cost, 0);
        const re = rows.reduce((s, r) => s + r.cells[i].revenue, 0);
        return { cost: co, revenue: re, roas: co > 0 ? re / co : null };
      }),
    };

    return { dayList: shown, matrix: rows, totalsRow: totals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, revenue, brandGroups, dayCount]);

  // Gün seçenekleri, tabloyu YATAY KAYDIRMADAN sığdırabildiğimiz kadar: her gün
  // 3 kolon demek, 14 günde 43 kolona çıkıyor ve ~1300px'lik alanda kolon başına
  // ~30px kalıyor. 30 gün (91 kolon) kaydırmasız okunaklı basılamıyor, bu yüzden
  // seçenek listesinde YOK — veri yine 30 günlük pencereden geliyor.
  const COUNT_OPTS = [7, 10, 14];
  const colCount = dayList.length * 3 + 1;
  // Sıkışık modda yazı/padding küçülür ve ROAS trendi sadece ok olarak basılır;
  // "▲ 12.14" 30px'lik kolonda alt satıra taşıyordu. Değer tooltip'te duruyor.
  const dense = dayCount >= 12;
  const numCls = dense ? 'px-0.5 text-[10px]' : 'px-1 text-xs';

  // ROAS trendi, sağdaki kolona (= bir önceki güne) göre.
  const RoasDelta = ({ cur, prev }) => {
    if (cur == null || prev == null || !(prev > 0)) return null;
    const diff = cur - prev;
    if (Math.abs(diff) < 0.005) return <div className="text-[10px] text-gray-400">≈</div>;
    const up = diff > 0;
    return (
      <div
        className={`text-[10px] font-semibold whitespace-nowrap ${up ? 'text-emerald-600' : 'text-red-500'}`}
        title={`bir önceki güne göre ${up ? '+' : '−'}${Math.abs(diff).toFixed(2)}x`}
      >
        {up ? '▲' : '▼'}{dense ? '' : ` ${Math.abs(diff).toFixed(2)}`}
      </div>
    );
  };

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
              <CalendarDays className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800">Marka Bazlı Günlük Harcama · BQ Ciro · ROAS</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Üstteki haftalık matrisin gün kırılımı · Bugünün kolonu GÜN İÇİ (kısmi) veridir · Tam değerler için hücrenin üzerine gel
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
              {COUNT_OPTS.map(o => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setDayCount(o)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    dayCount === o ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={`Son ${o} gün`}
                >
                  {`${o}g`}
                </button>
              ))}
            </div>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                title="Canlı veriyi yeniden çek"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Kaydırma YOK: tablo tamamı tek ekranda görünür. overflow-x-auto sadece
          dar ekranda taşmanın kırpılmaması için emniyet kemeri. */}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col style={{ width: dense ? '6.5rem' : '9rem' }} />
            {dayList.map(d => (
              <React.Fragment key={d}>
                <col /><col /><col />
              </React.Fragment>
            ))}
          </colgroup>
          <thead className="sticky top-0 bg-gray-50 z-20">
            <tr className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <th rowSpan={2} className="px-3 py-2 text-left border-r border-gray-200 sticky left-0 bg-gray-50 z-30">
                Marka
              </th>
              {dayList.map(d => {
                const l = dayLabel(d);
                const isToday = d === todayKey;
                return (
                  <th
                    key={d}
                    colSpan={3}
                    className={`${dense ? 'px-0.5 text-[10px]' : 'px-1'} py-2 text-center border-l border-gray-200 leading-tight ${l.weekend ? 'bg-gray-100' : ''}`}
                    title={isToday ? 'Bugün — gün içi, kısmi veri' : ''}
                  >
                    <div className={isToday ? 'text-indigo-600 font-semibold' : ''}>{l.date}</div>
                    <div className="text-[10px] font-normal normal-case text-gray-400">
                      {isToday ? 'bugün' : l.weekday}
                    </div>
                  </th>
                );
              })}
            </tr>
            <tr className={`font-medium text-gray-400 uppercase ${dense ? 'text-[9px] tracking-tight' : 'text-[10px] tracking-wide'}`}>
              {dayList.map(d => {
                const wknd = dayLabel(d).weekend ? 'bg-gray-100' : '';
                return (
                  <React.Fragment key={d}>
                    <th className={`${numCls} py-1.5 text-right border-l border-gray-200 ${wknd}`} title="Harcama (₺)">{dense ? 'Har' : 'Harc.'}</th>
                    <th className={`${numCls} py-1.5 text-right ${wknd}`} title="BQ Ciro (₺)">{dense ? 'Cir' : 'Ciro'}</th>
                    <th className={`${numCls} py-1.5 text-right ${wknd}`} title="ROAS">ROAS</th>
                  </React.Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={Math.max(colCount, 2)} className="py-8 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!loading && matrix.length === 0 && (
              <tr><td colSpan={Math.max(colCount, 2)} className="py-8 text-center text-gray-400">Veri yok</td></tr>
            )}
            {!loading && matrix.map(row => (
              <tr key={row.key} className="border-t border-gray-100 hover:bg-gray-50/50">
                <td className="px-3 py-2 font-medium text-gray-800 text-xs truncate border-r border-gray-200 sticky left-0 bg-white z-10" title={row.label}>
                  {row.label}
                </td>
                {row.cells.map((c, i) => (
                  <React.Fragment key={dayList[i]}>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-700 whitespace-nowrap border-l border-gray-200`} title={c.cost > 0 ? fmtFull(c.cost) : ''}>
                      {c.cost > 0 ? fmtK(c.cost) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-700 whitespace-nowrap`} title={c.revenue > 0 ? fmtFull(c.revenue) : ''}>
                      {c.revenue > 0 ? fmtK(c.revenue) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className={`${numCls} py-2 text-right tabular-nums font-semibold whitespace-nowrap ${roasCls(c.roas)}`} title={roasTitle(c.roas)}>
                      <div>{fmtRoas(c.roas)}</div>
                      <RoasDelta cur={c.roas} prev={row.cells[i + 1]?.roas ?? null} />
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
            {!loading && matrix.length > 0 && (
              <tr className="border-t-2 border-gray-300 bg-gray-50/70 font-semibold">
                <td className="px-3 py-2 text-xs text-gray-900 border-r border-gray-200 sticky left-0 bg-gray-50 z-10">
                  {totalsRow.label}
                </td>
                {totalsRow.cells.map((c, i) => (
                  <React.Fragment key={dayList[i]}>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-900 whitespace-nowrap border-l border-gray-200`} title={fmtFull(c.cost)}>{fmtK(c.cost)}</td>
                    <td className={`${numCls} py-2 text-right tabular-nums text-gray-900 whitespace-nowrap`} title={fmtFull(c.revenue)}>{fmtK(c.revenue)}</td>
                    <td className={`${numCls} py-2 text-right tabular-nums whitespace-nowrap ${roasCls(c.roas)}`} title={roasTitle(c.roas)}>
                      <div>{fmtRoas(c.roas)}</div>
                      <RoasDelta cur={c.roas} prev={totalsRow.cells[i + 1]?.roas ?? null} />
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Haftalık marka yorumu — ajansın haftalık mailinin veriden üretilen taslağı.
// Girdi yukarıdaki matrisin AYNISI (son 8 hafta), kurallar utils/high5NetSalesCommentary.js.
// Yalnız heyglowy.com kullanıcıları görür (çağıran yer kontrol ediyor).
// ---------------------------------------------------------------------------
// Emoji her ortamda çizilmiyor (emoji fontu olmayan sistemde kare kutu) → panelde renkli
// nokta; "Metni kopyala" çıktısında emoji kalır (mail istemcileri çizer).
const STATUS_DOT_CLS = { '🟢': 'bg-emerald-500', '🟡': 'bg-yellow-400', '🟠': 'bg-orange-500', '🔴': 'bg-red-500', '⚪': 'bg-gray-300' };

function BrandWeeklyNetSalesCommentaryCard({ weeks, netSales, loading, brandGroups }) {
  const [copied, setCopied] = useState(false);
  const commentary = useMemo(() => {
    const m = buildNetSalesMatrix({ weeks, netSales, brandGroups, weekCount: 8 });
    return buildNetSalesCommentary(m);
  }, [weeks, netSales, brandGroups]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(commentaryToText(commentary));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* pano izni yoksa sessiz geç */ }
  };

  const B = ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>;

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
              <FileText className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                HAFTALIK YORUM — MARKA BAZLI PERFORMANS
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-gray-200 bg-white text-gray-500">
                  <Lock className="w-3 h-3" /> yalnız heyglowy.com
                </span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {commentary
                  ? `${commentary.week}. hafta · son ${commentary.weeks} hafta · yukarıdaki tablodan otomatik üretilir, net satış yüklendikçe güncellenir`
                  : 'Yukarıdaki tablodan otomatik üretilir'}
              </p>
            </div>
          </div>
          <button
            onClick={copy}
            disabled={!commentary}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Kopyalandı' : 'Metni kopyala'}
          </button>
        </div>
      </div>

      <div className="px-6 py-5 text-sm text-gray-700 leading-relaxed space-y-4">
        {loading && !commentary && <div className="text-gray-400">Veriler yükleniyor…</div>}
        {!loading && !commentary && (
          <div className="text-gray-400">
            Yorum için en az iki haftanın net satışı ve harcaması gerekli.
          </div>
        )}
        {commentary && (
          <>
            <p>Son {commentary.weeks} haftalık marka performansı yatırım, net satış ve ROAS metrikleri üzerinden incelendi.</p>
            <p>{commentary.summary}</p>
            {commentary.trend && <p>{commentary.trend}</p>}
            {commentary.divergence && <p>{commentary.divergence}</p>}

            <div>
              <div className="font-semibold text-gray-900 mb-2">Öne Çıkan Değerlendirmeler</div>
              <div className="overflow-x-auto">
                <table className="text-sm">
                  <thead>
                    <tr className="text-left text-gray-900">
                      <th className="pr-6 py-1 font-semibold">Marka</th>
                      <th className="pr-6 py-1 font-semibold">Durum</th>
                      <th className="py-1 font-semibold">Öneri</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commentary.table.map((r) => (
                      <tr key={r.key}>
                        <td className="pr-6 py-1 font-semibold text-gray-900 whitespace-nowrap">{r.brand}</td>
                        <td className="pr-6 py-1 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT_CLS[r.dot] || 'bg-gray-300'}`} />
                            {r.label}
                          </span>
                        </td>
                        <td className="py-1">{r.advice}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <ul className="list-disc pl-5 space-y-1.5">
              {commentary.notes.map((n) => (
                <li key={n.key}><B>{n.brand}:</B> {n.text}</li>
              ))}
            </ul>

            <div>
              <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                Bu Hafta Planlanan Aksiyonlar
                {commentary.planActive && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700">
                    bütçe planı ekip tarafından girildi
                  </span>
                )}
              </div>
              <ul className="list-disc pl-5 space-y-1.5">
                {commentary.actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Matris hesabı bileşenin DIŞINDA: ekran (seçili hafta sayısı) ve Excel (sabit
// 8 hafta) aynı koddan beslensin diye. Excel, ekranda 4 hafta seçili olsa bile
// son 8 haftayı verir — dosyayı açan kişi ekranı görmüyor.
// ---------------------------------------------------------------------------
function buildNetSalesMatrix({ weeks, netSales, brandGroups, weekCount }) {
  const BRAND_GROUPS = brandGroups || [];
    const keys = [...BRAND_GROUPS.map(b => b.key), 'Other'];
    const cost = {};
    for (const w of weeks || []) {
      if (!w?.week) continue;
      cost[w.week] = w.brands || {};
    }

    // brand_key zaten BRAND_GROUPS anahtarı ('Other' dahil) → doğrudan kullanılır.
    const ns = {};
    for (const w of netSales?.rows || []) {
      if (!w?.week) continue;
      ns[w.week] = w.brands || {};
    }

    // SADECE net satış verisi yüklenmiş haftalar. Harcaması olan ama net satışı
    // olmayan hafta kolonu gösterilmez — ROAS'ı hesaplanamayacağı için kolon
    // "harcama var, satış yok" gibi yanlış okunuyordu.
    const allWeeks = Object.keys(ns)
      .filter(w => Object.values(ns[w] || {}).some(v => Number(v) !== 0))
      .sort((a, b) => b.localeCompare(a));
    const shown = allWeeks.slice(0, weekCount);

    const cell = (c, n) => {
      const co = Number(c) || 0;
      const amt = Number(n) || 0;
      // Net satış yoksa ROAS'ı 0,00 diye göstermek "sıfır satış" gibi okunur → "—".
      return { cost: co, netSales: amt, roas: co > 0 && amt !== 0 ? amt / co : null };
    };
    // Marka listesi TAM: veri olmasa da her marka kendi satırında görünür
    // (tabloda kaydırma yok, hepsi tek ekranda). Tek istisna, hiç veri gelmemiş
    // "Diğer" satırı — marka olmadığı için boşken gösterilmesi kafa karıştırıyor.
    const rows = keys.map(k => ({
      key: k,
      label: k === 'Other' ? 'Diğer (eşleşmeyen)' : (BRAND_GROUPS.find(b => b.key === k)?.label || k),
      cells: shown.map(w => cell(cost[w]?.[k], ns[w]?.[k])),
    })).filter(r => r.key !== 'Other' || r.cells.some(c => c.cost > 0 || c.netSales !== 0));

    // Sıralama: EN SON haftanın (kolonların en solu) net satışı, büyükten küçüğe.
    // Eskiden brandGroups.js'teki elle yazılmış liste sırasıydı — veriyle ilgisizdi.
    // O hafta satışı olmayan markalar dipte kalır; kendi aralarında görünen tüm
    // haftaların toplamına göre sıralanır ki rastgele bir sıra oluşmasın.
    // "Diğer" marka olmadığı için her zaman en altta.
    const sumNet = (r) => r.cells.reduce((s, c) => s + c.netSales, 0);
    rows.sort((a, b) => {
      if (a.key === 'Other') return 1;
      if (b.key === 'Other') return -1;
      const last = (b.cells[0]?.netSales || 0) - (a.cells[0]?.netSales || 0);
      if (last !== 0) return last;
      return sumNet(b) - sumNet(a);
    });

    const totals = {
      key: '__total__',
      label: 'TOPLAM',
      cells: shown.map((_, i) => {
        const co = rows.reduce((s, r) => s + r.cells[i].cost, 0);
        const amt = rows.reduce((s, r) => s + r.cells[i].netSales, 0);
        return { cost: co, netSales: amt, roas: co > 0 && amt !== 0 ? amt / co : null };
      }),
    };

    return { weekList: shown, matrix: rows, totalsRow: totals };
}

// ---------------------------------------------------------------------------
// BrandWeeklyRoasMatrixCard'ın ikizi: ciro kolonu yerine "Gerçekleşen Net Satış".
// Kaynak GA4 değil, ERP'nin haftalık Excel export'u (/high5/net-sales sayfasından
// yüklenir, erp_weekly_net_sales_by_brand tablosunda durur). Tutarlar KDV HARİÇ ve
// iadeler düşülmüş — bu yüzden BQ Ciro kartının yerine değil, yanına duruyor.
//
// Net satış payload'ı zaten BRAND_GROUPS anahtarlarıyla geliyor (brand_key import
// sırasında çözülüyor), bu yüzden burada matchBrand'e gerek YOK — 'Say Sky' gibi
// adlar matchBrand'in kelime sınırında zaten eşleşmiyordu.
// ---------------------------------------------------------------------------
// Dar ekran tespiti. Matrisin kaydırmalı mobil moduna GEÇİP geçmeyeceğini
// belirler; masaüstünde daima false döner, orada hiçbir şey değişmez.
// Eşik 1280: ÖLÇÜLDÜ — 8 haftalık matris 1024px'te 151 hücrede içeriği taşırıp
// okunmaz oluyor, 1280'de temiz. 1024'lük dizüstü de kaydırmalı moda girmeli.
function useIsNarrow(query = '(max-width: 1279px)') {
  const [narrow, setNarrow] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false)
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return narrow;
}

// defaultWeekCount: açılıştaki hafta sayısı. Panel içinde 6h (varsayılan),
// public rapor sayfası 8h açıyor — kullanıcı butonlardan yine değiştirebilir.
// mobileScroll: SADECE public paylaşım sayfası açar. Dar ekranda tablo gerçek
// genişliğinde kalıp yatay kayar, marka kolonu solda sabitlenir. Panelde kapalı
// — orada "kaydırma olmasın" kuralı geçerli (10 Ağu kararı).
export function BrandWeeklyNetSalesMatrixCard({ weeks, netSales, loading, onRefresh, brandGroups, brandId, defaultWeekCount = 6, mobileScroll = false }) {
  const [weekCount, setWeekCount] = useState(defaultWeekCount);
  const scrollMode = useIsNarrow() && mobileScroll;
  // Sabitlenen kolonun arka planı OPAK olmak ZORUNDA: yarı saydam bırakılırsa
  // altından kayan rakamlar okunur ve marka adıyla üst üste biner.
  const stickyHead = scrollMode ? 'sticky left-0 z-20 bg-gray-50' : '';
  const stickyCell = scrollMode ? 'sticky left-0 z-10 bg-white' : '';
  const stickyTotal = scrollMode ? 'sticky left-0 z-10 bg-gray-50' : '';

  const fmtK = (v) => {
    if (!v) return '—';
    if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
    if (v >= 1_000) return `${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}K`;
    return Math.round(v).toLocaleString('tr-TR');
  };
  const fmtFull = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
  const fmtRoas = (v) => (v == null ? '—' : v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const roasCls = (v) => (v == null ? 'text-gray-300' : v >= 3 ? 'text-emerald-600' : v >= 1 ? 'text-amber-600' : 'text-red-500');
  // Markanın o haftaki payı. Payda = haftanın TOPLAM satırı, yani tabloda görünen
  // markaların toplamı — böylece bir kolondaki yüzdeler %100'e tamamlanır.
  // Negatif net satışta (iade fazlası) yüzde de negatif gösterilir.
  const fmtShare = (v, total) => {
    if (!(total > 0) || !v) return null;
    const p = (v / total) * 100;
    const abs = Math.abs(p);
    const txt = abs.toLocaleString('tr-TR', {
      minimumFractionDigits: abs < 10 ? 1 : 0,
      maximumFractionDigits: abs < 10 ? 1 : 0,
    });
    return `${p < 0 ? '−' : ''}%${txt}`;
  };

  // week anahtarı her zaman ISO Pazartesi → o haftanın Perşembe'si hangi yıla
  // düşüyorsa ISO yılı odur; hafta no o yılın 4 Ocak'ını içeren haftadan sayılır.
  // ERP dosyasındaki "Tarih Hafta ID" ile birebir aynı numarayı verir.
  const isoWeekNo = (weekStart) => {
    const d = new Date(weekStart + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + 3); // Pazartesi → Perşembe
    const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    const week1Mon = new Date(jan4);
    week1Mon.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7));
    return 1 + Math.round((d - week1Mon) / (7 * 86400000));
  };
  const fmtRange = (weekStart) => {
    const s = new Date(weekStart + 'T00:00:00');
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    return { range: `${f(s)} – ${f(e)}`, year: e.getFullYear(), week: isoWeekNo(weekStart) };
  };

  const { weekList, matrix, totalsRow } = useMemo(
    () => buildNetSalesMatrix({ weeks, netSales, brandGroups, weekCount }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeks, netSales, brandGroups, weekCount]
  );

  const RoasDelta = ({ cur, prev }) => {
    if (cur == null || prev == null || !(prev > 0)) return null;
    const diff = cur - prev;
    if (Math.abs(diff) < 0.005) return <div className="text-[10px] text-gray-400">≈</div>;
    const up = diff > 0;
    return (
      <div
        className={`text-[10px] font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}
        title={`önceki haftaya göre ${up ? '+' : '−'}${Math.abs(diff).toFixed(2)}x`}
      >
        {up ? '▲' : '▼'} {Math.abs(diff).toFixed(2)}
      </div>
    );
  };

  const COUNT_OPTS = [4, 6, 8, 12];
  const colCount = weekList.length * 3 + 1;

  // İki sayfa: "Matris" ekrandaki tablonun birebir aynısı; "Detay" satır başına
  // tek (hafta × marka) kaydı olan uzun format — Excel'de pivot/grafik kurmak
  // isteyen için geniş tablodan çok daha kullanışlı.
  // Önce grafikli (sunucu) sürüm denenir; sunucuya ulaşılamazsa aşağıdaki
  // istemci-içi (grafiksiz) export devreye girer.
  const exportXlsx = async () => {
    // Ekranda kaç hafta seçili olursa olsun dosya SON 8 HAFTAYI içerir.
    // (Veri 8 haftadan azsa eldeki kadarı — olmayan hafta uydurulmaz.)
    const ex = buildNetSalesMatrix({ weeks, netSales, brandGroups, weekCount: NS_EXPORT_WEEKS });
    const exWeeks = ex.weekList;
    const exMatrix = ex.matrix;
    const exTotals = ex.totalsRow;
    if (!exWeeks.length) return;
    const ilkNo = fmtRange(exWeeks[exWeeks.length - 1]).week;
    const sonNo = fmtRange(exWeeks[0]).week;
    const fileName = `net-satis-haftalik-h${ilkNo}-h${sonNo}.xlsx`;
    if (brandId) {
      try {
        await downloadChartedXlsx({
          brandId,
          fileName,
          payload: {
            kind: 'matrix',
            weekList: exWeeks.map(w => ({ week: w, no: fmtRange(w).week, range: fmtRange(w).range })),
            rows: exMatrix.map(r => ({ label: r.label, cells: r.cells })),
            totals: { label: exTotals.label, cells: exTotals.cells },
          },
        });
        return;
      } catch (e) {
        console.warn('[net-satis] grafikli export başarısız, istemci export kullanılıyor', e);
      }
    }
    const pct = (v, total) => (total > 0 && v ? Number((v / total * 100).toFixed(2)) : null);
    const round = (v) => (v ? Number(Number(v).toFixed(2)) : 0);

    const head = ['Marka'];
    for (const w of exWeeks) {
      const l = fmtRange(w);
      head.push(
        `${l.week}. hafta (${l.range}) · Harcama`,
        `${l.week}. hafta · Harcama payı %`,
        `${l.week}. hafta · Net satış`,
        `${l.week}. hafta · Net satış payı %`,
        `${l.week}. hafta · ROAS`,
      );
    }
    const lineFor = (row) => {
      const out = [row.label];
      row.cells.forEach((c, i) => {
        const wk = exTotals.cells[i];
        out.push(
          round(c.cost),
          pct(c.cost, wk.cost),
          round(c.netSales),
          pct(c.netSales, wk.netSales),
          c.roas == null ? null : Number(c.roas.toFixed(2)),
        );
      });
      return out;
    };
    const wide = [head, ...exMatrix.map(lineFor), lineFor(exTotals)];

    const long = [[
      'Hafta no', 'Hafta başlangıcı', 'Hafta aralığı', 'Marka',
      'Harcama', 'Harcama payı %', 'Net satış', 'Net satış payı %', 'ROAS',
    ]];
    exWeeks.forEach((w, i) => {
      const l = fmtRange(w);
      const wk = exTotals.cells[i];
      for (const row of exMatrix) {
        const c = row.cells[i];
        if (!c.cost && !c.netSales) continue;   // boş hücreyi satır olarak yazma
        long.push([
          l.week, w, l.range, row.label,
          round(c.cost), pct(c.cost, wk.cost),
          round(c.netSales), pct(c.netSales, wk.netSales),
          c.roas == null ? null : Number(c.roas.toFixed(2)),
        ]);
      }
    });

    const wb = XLSX.utils.book_new();
    const wsWide = XLSX.utils.aoa_to_sheet(wide);
    wsWide['!cols'] = [{ wch: 20 }, ...exWeeks.flatMap(() => [{ wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 17 }, { wch: 8 }])];
    XLSX.utils.book_append_sheet(wb, wsWide, 'Matris');

    const wsLong = XLSX.utils.aoa_to_sheet(long);
    wsLong['!cols'] = [{ wch: 8 }, { wch: 15 }, { wch: 16 }, { wch: 20 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 17 }, { wch: 8 }];
    XLSX.utils.book_append_sheet(wb, wsLong, 'Detay');

    XLSX.writeFile(wb, fileName);
  };


  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
        <div className={`flex justify-between gap-3 ${mobileScroll ? 'flex-col items-start lg:flex-row lg:items-center' : 'items-center'}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
              <Layers className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              {/* Başlıklar BÜYÜK HARF yazılır, CSS `uppercase` ile DEĞİL:
                  text-transform Türkçe "i"yi "I" yapıp "GRAFIKLERI" üretiyor. */}
              <h2 className="font-semibold text-gray-800">MARKA BAZLI HAFTALIK HARCAMA · GERÇEKLEŞEN NET SATIŞ · ROAS</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Satırlar marka, kolonlar hafta · Tutarlar ₺ (K = bin, M = milyon; tam değer için hücrenin üzerine gel) ·
                Harcama live Google+Meta, net satış ERP (KDV hariç, iadeler düşülmüş)
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Sıralama: en son haftanın net satışı, çoktan aza ·
                Rakamın altındaki yüzde, markanın o haftanın toplamındaki payı (harcamada ve net satışta ayrı ayrı) ·
                Sadece net satış verisi yüklü haftalar gösterilir
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-2 ${mobileScroll ? 'flex-wrap' : ''}`}>
            <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
              {COUNT_OPTS.map(o => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setWeekCount(o)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    weekCount === o ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={`Son ${o} hafta`}
                >
                  {`${o}h`}
                </button>
              ))}
            </div>
            <button
              onClick={exportXlsx}
              disabled={loading || !weekList.length}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
              title="Son 8 haftayı grafikli Excel indir (Matris + Detay + Haftalık Toplam grafiği)"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
                title="Net satış verisini yeniden çek"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Dikey kaydırma YOK: marka sayısı sabit ve hepsinin tek ekranda
          görünmesi isteniyor — liste kısaltılmıyor, kaydırılmıyor.
          Yatay kaydırma da sadece dar ekranda + mobileScroll açıkken. */}
      <div className={scrollMode ? 'overflow-x-auto' : ''}>
        {/* min-width hafta sayısıyla büyür (hafta başına 3 kolon = 9rem). Sabit
            bir değer verilirse 4h'te boşluk, 12h'te yine sıkışma olurdu. */}
        <table
          className="w-full table-fixed text-sm"
          style={scrollMode ? { minWidth: `${7 + weekList.length * 9}rem` } : undefined}
        >
          <colgroup>
            <col style={{ width: scrollMode ? '7rem' : '9rem' }} />
            {weekList.map(w => (
              <React.Fragment key={w}>
                <col /><col /><col />
              </React.Fragment>
            ))}
          </colgroup>
          <thead className="bg-gray-50">
            <tr className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <th rowSpan={2} className={`px-3 py-2 text-left border-r border-gray-200 ${stickyHead}`}>
                Marka
              </th>
              {weekList.map(w => {
                const l = fmtRange(w);
                return (
                  <th
                    key={w}
                    colSpan={3}
                    className="px-1 py-2 text-center border-l border-gray-200 leading-tight"
                    title={`${l.year} ISO haftası ${l.week} · ${l.range}`}
                  >
                    <div className="text-gray-700">{l.week}. hafta</div>
                    <div className="text-[10px] font-normal text-gray-400 normal-case">{l.range}</div>
                  </th>
                );
              })}
            </tr>
            <tr className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
              {weekList.map(w => (
                <React.Fragment key={w}>
                  <th className="px-1 py-1.5 text-right border-l border-gray-200" title="Harcama (₺)">Harc.</th>
                  <th className="px-1 py-1.5 text-right" title="Gerçekleşen Net Satış (₺)">Net Satış</th>
                  <th className="px-1 py-1.5 text-right" title="ROAS (net satış / harcama)">ROAS</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={colCount} className="py-8 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {/* Kolon listesi net satış verisinden türüyor — hiç hafta yoksa
                satırları basmanın anlamı yok. */}
            {!loading && weekList.length === 0 && (
              <tr><td colSpan={colCount} className="py-8 text-center text-gray-400">Yüklü net satış verisi yok — Net Satış Yükleme sayfasından Excel yükleyin.</td></tr>
            )}
            {!loading && weekList.length > 0 && matrix.map(row => (
              <tr key={row.key} className="border-t border-gray-100 hover:bg-gray-50/50">
                <td className={`px-3 py-2 font-medium text-gray-800 text-xs truncate border-r border-gray-200 ${stickyCell}`} title={row.label}>
                  {row.label}
                </td>
                {row.cells.map((c, i) => {
                  // Pay = markanın o haftaki değeri / haftanın TOPLAMI (TOPLAM satırı).
                  const wk = totalsRow.cells[i];
                  return (
                  <React.Fragment key={weekList[i]}>
                    <td className="px-1 py-2 text-right tabular-nums text-xs text-gray-700 border-l border-gray-100" title={c.cost > 0 ? `${fmtFull(c.cost)} · haftanın harcamasının ${fmtShare(c.cost, wk.cost) || '—'}` : ''}>
                      {c.cost > 0 ? (
                        <>
                          <div>{fmtK(c.cost)}</div>
                          <div className="text-[10px] font-normal text-gray-400">{fmtShare(c.cost, wk.cost)}</div>
                        </>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    {/* Negatif = o hafta iadeler satışı aştı; kırmızı ve eksi işaretiyle gösterilir. */}
                    <td
                      className={`px-1 py-2 text-right tabular-nums text-xs ${c.netSales < 0 ? 'text-red-500' : 'text-gray-700'}`}
                      title={c.netSales !== 0 ? `${fmtFull(c.netSales)} · haftanın net satışının ${fmtShare(c.netSales, wk.netSales) || '—'}` : 'bu hafta için net satış kaydı yok'}
                    >
                      {c.netSales !== 0 ? (
                        <>
                          <div>{`${c.netSales < 0 ? '−' : ''}${fmtK(Math.abs(c.netSales))}`}</div>
                          <div className={`text-[10px] font-normal ${c.netSales < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                            {fmtShare(c.netSales, wk.netSales)}
                          </div>
                        </>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className={`px-1 py-2 text-right tabular-nums text-xs font-semibold ${roasCls(c.roas)}`}>
                      <div>{fmtRoas(c.roas)}</div>
                      <RoasDelta cur={c.roas} prev={row.cells[i + 1]?.roas ?? null} />
                    </td>
                  </React.Fragment>
                  );
                })}
              </tr>
            ))}
            {!loading && weekList.length > 0 && matrix.length > 0 && (
              <tr className="border-t-2 border-gray-300 bg-gray-50/70 font-semibold">
                <td className={`px-3 py-2 text-xs text-gray-900 border-r border-gray-200 ${stickyTotal}`}>
                  {totalsRow.label}
                </td>
                {totalsRow.cells.map((c, i) => (
                  <React.Fragment key={weekList[i]}>
                    <td className="px-1 py-2 text-right tabular-nums text-xs text-gray-900 border-l border-gray-100" title={fmtFull(c.cost)}>{fmtK(c.cost)}</td>
                    <td className={`px-1 py-2 text-right tabular-nums text-xs ${c.netSales < 0 ? 'text-red-500' : 'text-gray-900'}`} title={fmtFull(c.netSales)}>
                      {c.netSales !== 0 ? `${c.netSales < 0 ? '−' : ''}${fmtK(Math.abs(c.netSales))}` : '—'}
                    </td>
                    <td className={`px-1 py-2 text-right tabular-nums text-xs ${roasCls(c.roas)}`}>
                      <div>{fmtRoas(c.roas)}</div>
                      <RoasDelta cur={c.roas} prev={totalsRow.cells[i + 1]?.roas ?? null} />
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Grafikli Excel: tarayıcıdaki xlsx kütüphanesi hücre yazar ama GRAFİK GÖMEMEZ.
// Bu yüzden ekranda görünen veri backend'e post edilir, openpyxl gerçek Excel
// grafiklerini (tıklanabilir, hücrelere bağlı) üretip dosyayı geri verir.
// Sunucu bir sebeple cevap veremezse çağıran taraf kendi istemci-içi (grafiksiz)
// export'una düşer — buton hiçbir durumda ölü kalmaz.
// ---------------------------------------------------------------------------
async function downloadChartedXlsx({ brandId, payload, fileName }) {
  const r = await fetch(`${API_BASE}/api/high5-net-sales/export-xlsx`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
    body: JSON.stringify({ ...payload, fileName }),
  });
  if (!r.ok) throw new Error(`export-xlsx ${r.status}`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Net satış grafikleri — yukarıdaki matrisin görselleştirilmiş hâli.
//
// Renk kuralları (dataviz):
//  · Kategorik palet doğrulandı (validate_palette, light): 7 slot, en kötü komşu
//    CVD ΔE 9.1 / normal-görü ΔE 19.6 → geçti. 3 renk yüzey kontrastında 3:1'in
//    altında; "relief" şartı hemen ÜSTTEKİ tablo (tablo görünümü) ile karşılanıyor.
//  · Renk MARKAYA bağlı, sıraya değil: slot ataması tüm yüklü haftaların toplamına
//    göre yapılır, hafta filtresi (4h/6h/…) değişince markanın rengi DEĞİŞMEZ.
//  · 8+ marka üretilmiş renge kaçmaz; kuyruk "Diğer markalar" (nötr gri) altında toplanır.
//  · Tek eksen — çift y ekseni yok. Harcama ve net satış aynı grafikte
//    karşılaştırılacağı yerde ikisi de YÜZDEye çevrilir (aynı birim).
// ---------------------------------------------------------------------------
const NS_SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const NS_OTHER_COLOR = '#7c7c78';   // kuyruk kovası — kimlik değil, nötr
const NS_COST_COLOR = '#2a78d6';    // slot 1
const NS_SALES_COLOR = '#eb6834';   // slot 2 (all-pairs doğrulandı: CVD ΔE 24.7)
const NS_MAX_SERIES = 7;
// Excel her zaman SON 8 HAFTA — ekrandaki hafta seçicisinden bağımsız.
// Dosyayı açan kişi ekranı görmüyor, sabit bir pencere bekliyor.
const NS_EXPORT_WEEKS = 8;

// ---------------------------------------------------------------------------
// Grafik verisi de bileşenin DIŞINDA — ekran seçili hafta sayısıyla, Excel
// sabit 8 haftayla aynı fonksiyonu çağırır.
// weekLabel bağımsız: hafta etiketi ISO hafta no üretir.
// ---------------------------------------------------------------------------
function buildNetSalesCharts({ weeks, netSales, brandGroups, weekCount, weekLabel }) {
  const BRAND_GROUPS = brandGroups || [];
    const cost = {};
    for (const w of weeks || []) if (w?.week) cost[w.week] = w.brands || {};
    const ns = {};
    for (const w of netSales?.rows || []) if (w?.week) ns[w.week] = w.brands || {};

    const allWeeks = Object.keys(ns)
      .filter(w => Object.values(ns[w] || {}).some(v => Number(v) !== 0))
      .sort((a, b) => a.localeCompare(b));               // grafikte eskiden yeniye
    if (!allWeeks.length) {
      return { series: [], trend: [], shareRows: [], lastWeek: null, lastWeekRows: [], hasData: false };
    }
    const shown = allWeeks.slice(-weekCount);

    // Renk/seri ataması TÜM yüklü pencerenin toplamına göre — hafta filtresi
    // değiştiğinde seriler de renkler de sabit kalsın diye (recolor-on-filter yok).
    const keys = [...BRAND_GROUPS.map(b => b.key), 'Other'];
    const totalOf = {};
    for (const k of keys) {
      totalOf[k] = allWeeks.reduce((s, w) => s + Number(ns[w]?.[k] || 0), 0);
    }
    const ranked = keys
      .filter(k => k !== 'Other' && totalOf[k] !== 0)
      .sort((a, b) => totalOf[b] - totalOf[a]);
    const top = ranked.slice(0, NS_MAX_SERIES);
    const tail = [...ranked.slice(NS_MAX_SERIES), 'Other'];
    const labelOf = (k) => BRAND_GROUPS.find(b => b.key === k)?.label || k;

    const colorMap = new Map(top.map((k, i) => [k, NS_SERIES_COLORS[i]]));
    const seriesList = [
      ...top.map(k => ({ key: k, label: labelOf(k), color: colorMap.get(k) })),
      ...(tail.some(k => totalOf[k] !== 0)
        ? [{ key: '__tail__', label: `Diğer markalar (${tail.filter(k => totalOf[k] !== 0).length})`, color: NS_OTHER_COLOR }]
        : []),
    ];

    const tailSum = (w) => tail.reduce((s, k) => s + Number(ns[w]?.[k] || 0), 0);

    const trendData = shown.map(w => {
      const row = { week: w, label: weekLabel(w) };
      for (const k of top) row[k] = Number(ns[w]?.[k] || 0);
      row.__tail__ = tailSum(w);
      return row;
    });

    // %100 yığılmış grafik: negatifler (iade fazlası) payda ile oynayıp yüzdeyi
    // bozacağı için |değer| üzerinden pay alınır, tooltip'te ham ₺ gösterilir.
    const shareData = shown.map(w => {
      const raw = { __week: w, label: weekLabel(w) };
      for (const k of top) raw[k] = Number(ns[w]?.[k] || 0);
      raw.__tail__ = tailSum(w);
      const denom = seriesList.reduce((s, sr) => s + Math.abs(raw[sr.key] || 0), 0);
      const out = { week: w, label: raw.label, __raw: raw };
      for (const sr of seriesList) out[sr.key] = denom > 0 ? Math.abs(raw[sr.key] || 0) / denom * 100 : 0;
      return out;
    });

    // Son hafta: harcama payı ↔ net satış payı. İki farklı birim tek grafiğe
    // girmesin diye ikisi de yüzde (tek eksen kuralı).
    const lw = shown[shown.length - 1];
    const costTotal = keys.reduce((s, k) => s + Number(cost[lw]?.[k] || 0), 0);
    const salesTotal = keys.reduce((s, k) => s + Math.abs(Number(ns[lw]?.[k] || 0)), 0);
    const lwRows = keys
      .map(k => ({
        key: k,
        label: k === 'Other' ? 'Diğer' : labelOf(k),
        cost: Number(cost[lw]?.[k] || 0),
        sales: Number(ns[lw]?.[k] || 0),
        costPct: costTotal > 0 ? Number(cost[lw]?.[k] || 0) / costTotal * 100 : 0,
        salesPct: salesTotal > 0 ? Math.abs(Number(ns[lw]?.[k] || 0)) / salesTotal * 100 : 0,
      }))
      .filter(r => r.cost > 0 || r.sales !== 0)
      .sort((a, b) => b.salesPct - a.salesPct);

    return {
      series: seriesList,
      trend: trendData,
      shareRows: shareData,
      lastWeek: lw,
      lastWeekRows: lwRows,
      hasData: true,
    };
}

export function BrandWeeklyNetSalesChartsCard({ weeks, netSales, loading, brandGroups, brandId, defaultWeekCount = 8, mobileScroll = false }) {
  const [weekCount, setWeekCount] = useState(defaultWeekCount);
  // Dar ekranda efsane (legend) 1 değil 3 satıra çıkar; 30px'lik sabit yükseklik
  // onu çizim alanının ÜSTÜNE bindiriyordu. Efsaneye de grafiğe de yer aç.
  const narrow = useIsNarrow() && mobileScroll;
  const legendH = narrow ? 76 : 30;

  const fmtK = (v) => {
    const a = Math.abs(v);
    if (!v) return '0';
    const sign = v < 0 ? '−' : '';
    if (a >= 1_000_000) return `${sign}${(a / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
    if (a >= 1_000) return `${sign}${(a / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}K`;
    return `${sign}${Math.round(a).toLocaleString('tr-TR')}`;
  };
  const fmtTL = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
  const fmtPct = (v) => `%${Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`;
  const weekLabel = (weekStart) => {
    const d = new Date(weekStart + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + 3);
    const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    const w1 = new Date(jan4);
    w1.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7));
    return `${1 + Math.round((d - w1) / (7 * 86400000))}. H`;
  };

  const { series, trend, shareRows, lastWeek, lastWeekRows, hasData } = useMemo(
    () => buildNetSalesCharts({ weeks, netSales, brandGroups, weekCount, weekLabel }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeks, netSales, brandGroups, weekCount]
  );

  const TooltipBox = ({ title, rows }) => (
    <div className="rounded-xl border border-gray-200 bg-white shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-gray-800 mb-1">{title}</div>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: r.color }} />
          <span className="text-gray-600 flex-1">{r.label}</span>
          <span className="tabular-nums font-medium text-gray-900">{r.value}</span>
        </div>
      ))}
    </div>
  );

  const trendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
    return <TooltipBox title={label} rows={sorted.map(p => ({
      color: p.color, label: p.name, value: fmtTL(p.value),
    }))} />;
  };

  const shareTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const raw = payload[0]?.payload?.__raw || {};
    const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
    return <TooltipBox title={label} rows={sorted.map(p => ({
      color: p.color, label: p.name, value: `${fmtPct(p.value)} · ${fmtTL(raw[p.dataKey])}`,
    }))} />;
  };

  const lastWeekTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const r = payload[0]?.payload;
    if (!r) return null;
    return <TooltipBox title={r.label} rows={[
      { color: NS_COST_COLOR, label: 'Harcama payı', value: `${fmtPct(r.costPct)} · ${fmtTL(r.cost)}` },
      { color: NS_SALES_COLOR, label: 'Net satış payı', value: `${fmtPct(r.salesPct)} · ${fmtTL(r.sales)}` },
    ]} />;
  };

  const COUNT_OPTS = [6, 8, 12, 20];
  const axis = { stroke: '#d1d5db', tick: { fill: '#6b7280', fontSize: 11 }, tickLine: false };

  // Her grafik kendi sayfasına — Excel'de aynı veriden kendi grafiğini kurabilsin
  // diye ham sayılar yazılır (yüzde işareti metin olarak değil, sayı olarak).
  const exportXlsx = async () => {
    // Ekrandaki hafta seçicisi ne olursa olsun dosya SON 8 HAFTAYI içerir.
    const ex = buildNetSalesCharts({
      weeks, netSales, brandGroups, weekCount: NS_EXPORT_WEEKS, weekLabel,
    });
    if (!ex.hasData) return;
    const exSeries = ex.series;
    const fileName = `net-satis-grafikler-son${ex.trend.length}hafta.xlsx`;
    if (brandId) {
      try {
        await downloadChartedXlsx({
          brandId,
          fileName,
          payload: {
            kind: 'charts',
            series: exSeries.map(s => ({ key: s.key, label: s.label, color: s.color })),
            trend: ex.trend.map(r => ({
              label: r.label, week: r.week,
              values: Object.fromEntries(exSeries.map(s => [s.key, r[s.key]])),
            })),
            share: ex.shareRows.map(r => ({
              label: r.label, week: r.week,
              values: Object.fromEntries(exSeries.map(s => [s.key, r[s.key]])),
            })),
            lastWeek: { label: ex.lastWeek ? weekLabel(ex.lastWeek) : 'Son hafta', rows: ex.lastWeekRows },
          },
        });
        return;
      } catch (e) {
        console.warn('[net-satis] grafikli export başarısız, istemci export kullanılıyor', e);
      }
    }
    const round = (v, d = 2) => (v == null ? null : Number(Number(v).toFixed(d)));
    const wb = XLSX.utils.book_new();

    // 1) Son hafta: harcama payı ↔ net satış payı
    const s1 = [['Marka', 'Harcama', 'Harcama payı %', 'Net satış', 'Net satış payı %', 'Fark (satış−harcama) puan']];
    for (const r of ex.lastWeekRows) {
      s1.push([r.label, round(r.cost), round(r.costPct), round(r.sales), round(r.salesPct), round(r.salesPct - r.costPct)]);
    }
    const ws1 = XLSX.utils.aoa_to_sheet(s1);
    ws1['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 17 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Son Hafta Pay');

    // 2) Haftalık net satış trendi — grafikteki serilerin birebir aynısı
    const s2 = [['Hafta', 'Hafta başlangıcı', ...exSeries.map(s => s.label)]];
    for (const row of ex.trend) {
      s2.push([row.label, row.week, ...exSeries.map(s => round(row[s.key]))]);
    }
    const ws2 = XLSX.utils.aoa_to_sheet(s2);
    ws2['!cols'] = [{ wch: 10 }, { wch: 16 }, ...exSeries.map(() => ({ wch: 15 }))];
    XLSX.utils.book_append_sheet(wb, ws2, 'Trend');

    // 3) Haftalık pay yüzdeleri (%100'e tamamlanır)
    const s3 = [['Hafta', 'Hafta başlangıcı', ...exSeries.map(s => `${s.label} %`)]];
    for (const row of ex.shareRows) {
      s3.push([row.label, row.week, ...exSeries.map(s => round(row[s.key]))]);
    }
    const ws3 = XLSX.utils.aoa_to_sheet(s3);
    ws3['!cols'] = [{ wch: 10 }, { wch: 16 }, ...exSeries.map(() => ({ wch: 15 }))];
    XLSX.utils.book_append_sheet(wb, ws3, 'Pay Yuzdesi');

    XLSX.writeFile(wb, fileName);
  };

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800">NET SATIŞ GRAFİKLERİ — MARKA MARKA</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Yukarıdaki tablonun görselleştirilmiş hâli · Marka renkleri sabit, hafta filtresi değişince değişmez
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-2 ${mobileScroll ? 'flex-wrap' : ''}`}>
            <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
              {COUNT_OPTS.map(o => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setWeekCount(o)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    weekCount === o ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={`Son ${o} hafta`}
                >
                  {`${o}h`}
                </button>
              ))}
            </div>
            <button
              onClick={exportXlsx}
              disabled={loading || !hasData}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
              title="Son 8 haftanın 3 grafiğini Excel indir (grafikler dosyaya gömülü)"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="py-16 text-center text-gray-400 text-sm">Yükleniyor…</div>}
      {!loading && !hasData && (
        <div className="py-16 text-center text-gray-400 text-sm">
          Yüklü net satış verisi yok — Net Satış Yükleme sayfasından Excel yükleyin.
        </div>
      )}

      {!loading && hasData && (
        <div className="p-6 space-y-8">
          {/* 1) Asıl soru: hangi marka harcamadan aldığı paydan fazlasını satışta
                getiriyor? İki ölçü de yüzde olduğu için tek eksende karşılaştırılabilir. */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              SON HAFTA ({lastWeek ? weekLabel(lastWeek) : '—'}): HARCAMA PAYI ↔ NET SATIŞ PAYI
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">
              Turuncu çubuk mavinin sağını geçiyorsa marka, aldığı harcama payından fazlasını satışa çeviriyor.
            </p>
            <ResponsiveContainer width="100%" height={Math.max(220, lastWeekRows.length * 30 + 40)}>
              <BarChart data={lastWeekRows} layout="vertical" margin={{ top: 4, right: 48, bottom: 4, left: 4 }} barGap={2}>
                <CartesianGrid horizontal={false} stroke="#f1f2f4" />
                <XAxis type="number" unit="%" {...axis} axisLine={false} />
                <YAxis type="category" dataKey="label" width={96} {...axis} axisLine={false} />
                <RTooltip content={lastWeekTooltip} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  height={28}
                  iconType="square"
                  wrapperStyle={{ fontSize: 12, color: '#4b5563' }}
                />
                <Bar dataKey="costPct" name="Harcama payı" fill={NS_COST_COLOR} radius={[0, 4, 4, 0]} maxBarSize={11} />
                <Bar dataKey="salesPct" name="Net satış payı" fill={NS_SALES_COLOR} radius={[0, 4, 4, 0]} maxBarSize={11}>
                  <LabelList dataKey="salesPct" position="right" formatter={(v) => (v >= 1 ? fmtPct(v) : '')}
                    style={{ fill: '#6b7280', fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 2) Zaman içinde marka bazlı net satış seyri. */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800">HAFTALIK NET SATIŞ TRENDİ (₺)</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">
              En büyük {NS_MAX_SERIES} marka ayrı çizgi; kalanlar tek "Diğer markalar" çizgisinde toplanır.
            </p>
            <ResponsiveContainer width="100%" height={320 + (narrow ? legendH - 30 : 0)}>
              <LineChart data={trend} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid vertical={false} stroke="#f1f2f4" />
                <XAxis dataKey="label" {...axis} axisLine={false} />
                <YAxis tickFormatter={fmtK} {...axis} axisLine={false} width={52} />
                <RTooltip content={trendTooltip} cursor={{ stroke: '#9ca3af', strokeDasharray: '3 3' }} />
                <Legend verticalAlign="top" height={legendH} iconType="plainline" wrapperStyle={{ fontSize: narrow ? 11 : 12, color: '#4b5563' }} />
                {series.map(s => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={{ r: 2.5, strokeWidth: 0, fill: s.color }}
                    activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* 3) Payların zaman içinde nasıl kaydığı. */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800">HAFTALIK NET SATIŞ PAYI (%)</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">
              Her hafta %100'e tamamlanır · Negatif satır (iade fazlası) mutlak değeriyle paya girer, tooltip ham tutarı gösterir.
            </p>
            <ResponsiveContainer width="100%" height={300 + (narrow ? legendH - 30 : 0)}>
              {/* stackOffset="expand" YOK ve olmamalı: shareData zaten yüzdeye
                  çevrilmiş (denom'a bölünüp ×100). "expand" bir daha normalize
                  edip değerleri 0–1'e indiriyor, eksen 0–100 olduğu için tüm
                  çubuklar en alta yapışıp grafik boş görünüyordu. */}
              <BarChart data={shareRows} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid vertical={false} stroke="#f1f2f4" />
                <XAxis dataKey="label" {...axis} axisLine={false} />
                <YAxis tickFormatter={(v) => `%${Math.round(v)}`} domain={[0, 100]} {...axis} axisLine={false} width={44} />
                <RTooltip content={shareTooltip} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Legend verticalAlign="top" height={legendH} iconType="square" wrapperStyle={{ fontSize: narrow ? 11 : 12, color: '#4b5563' }} />
                {series.map(s => (
                  // stroke = beyaz ayraç: yığılmış dilimler arasında 2px yüzey boşluğu
                  <Bar key={s.key} dataKey={s.key} name={s.label} stackId="pay"
                    fill={s.color} stroke="#fff" strokeWidth={1} maxBarSize={44} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Unmatched (brand-less) ad campaigns × GA4 revenue, for the selected date range.
// Lists the individual campaigns behind the spend pivot's "Diğer" bucket — i.e.
// paid campaigns whose name carries no brand keyword — and how much GA4 revenue
// each drove. Brand filtering reuses the page's matchBrand/BRAND_GROUPS so the
// "unmatched" definition stays identical to the spend table.
// ---------------------------------------------------------------------------
function UnmatchedCampaignsCard({ data, loading, onRefresh, brandGroups, dateRangeLabel }) {
  const BRAND_GROUPS = brandGroups || [];
  const fmtTL = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
  const platformLabel = (p) => (PLATFORM_BADGE[p]?.label) || (p === 'TIKTOK_ADS' ? 'TikTok' : 'Diğer');

  const rows = useMemo(() => {
    const all = data?.campaigns || [];
    // Keep only campaigns that match NO brand key — the "Diğer" set.
    return all.filter((c) => !BRAND_GROUPS.some((b) => matchBrand(c.campaign, b.match || b.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, brandGroups]);

  const totalRevenue = useMemo(() => rows.reduce((s, r) => s + (Number(r.revenue) || 0), 0), [rows]);
  const totalCost = useMemo(() => rows.reduce((s, r) => s + (Number(r.cost) || 0), 0), [rows]);
  const totalRoas = totalCost > 0 ? totalRevenue / totalCost : null;
  const fmtRoas = (v) => (v == null ? '—' : `${v.toFixed(2)}x`);
  const roasCls = (v) => (v == null ? 'text-gray-300' : v >= 1 ? 'text-emerald-600' : 'text-red-500');

  const exportXlsx = () => {
    if (!rows.length) return;
    const aoa = [
      ['#', 'Kampanya', 'Platform', 'Harcama', 'GA4 Ciro', 'ROAS', 'İşlem'],
      ...rows.map((r, i) => [i + 1, r.campaign, platformLabel(r.platform), Math.round(Number(r.cost) || 0), Math.round(Number(r.revenue) || 0), r.cost > 0 ? Number((r.revenue / r.cost).toFixed(2)) : null, Number(r.transactions) || 0]),
      ['', 'TOPLAM', '', Math.round(totalCost), Math.round(totalRevenue), totalRoas != null ? Number(totalRoas.toFixed(2)) : null, rows.reduce((s, r) => s + (Number(r.transactions) || 0), 0)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 4 }, { wch: 42 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Eşleşmeyen Kampanyalar');
    XLSX.writeFile(wb, `esleemeyen-kampanyalar.xlsx`);
  };

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl border border-amber-200 bg-amber-50 flex items-center justify-center">
              <Layers className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800">Eşleşmeyen Kampanyalar — Harcama & GA4 Ciro</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {dateRangeLabel ? `${dateRangeLabel} · ` : ''}İçinde marka adı geçmeyen reklam kampanyaları · harcama (unified) + GA4 trafik atıflı ciro · ROAS
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportXlsx}
              disabled={loading || !rows.length}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
              title="Excel indir"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
                title="Veriyi yeniden çek"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[560px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-3 text-left whitespace-nowrap w-10">#</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">Kampanya</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">Platform</th>
              <th className="px-4 py-3 text-right whitespace-nowrap">Harcama</th>
              <th className="px-4 py-3 text-right whitespace-nowrap border-l border-gray-200">GA4 Ciro</th>
              <th className="px-4 py-3 text-right whitespace-nowrap">ROAS</th>
              <th className="px-4 py-3 text-right whitespace-nowrap">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="py-8 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-gray-400">Seçili aralıkta markasız kampanya bulunamadı</td></tr>
            )}
            {!loading && rows.map((r, i) => {
              const badge = PLATFORM_BADGE[r.platform];
              return (
                <tr key={r.campaign} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-gray-400 tabular-nums">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800 break-all max-w-[380px]">{r.campaign}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${badge?.cls || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${badge?.dot || 'bg-gray-400'}`} />
                      {platformLabel(r.platform)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">{r.cost > 0 ? fmtTL(r.cost) : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900 border-l border-gray-100">{r.revenue > 0 ? fmtTL(r.revenue) : <span className="text-gray-300">—</span>}</td>
                  <td className={`px-4 py-3 text-right tabular-nums font-semibold ${roasCls(r.roas)}`}>{fmtRoas(r.roas)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-500">{Number(r.transactions) || 0}</td>
                </tr>
              );
            })}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-gray-50">
              <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
                <td className="px-4 py-3" />
                <td className="px-4 py-3">TOPLAM ({rows.length} kampanya)</td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right tabular-nums">{fmtTL(totalCost)}</td>
                <td className="px-4 py-3 text-right tabular-nums border-l border-gray-200">{fmtTL(totalRevenue)}</td>
                <td className={`px-4 py-3 text-right tabular-nums ${roasCls(totalRoas)}`}>{fmtRoas(totalRoas)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{rows.reduce((s, r) => s + (Number(r.transactions) || 0), 0)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Brand breakdown: sums effective daily budgets per brand keyword × platform.
// ---------------------------------------------------------------------------
function BrandBreakdownCard({ breakdown, loading, dateRangeLabel }) {
  const { rows, other, totals } = breakdown;
  const hasOther = other.campaigns > 0;

  // Sort state for brand table (separate from platform tables).
  // Default: total budget descending — so brands sort top-to-bottom by budget share.
  const [sortKey, setSortKey] = React.useState('total');
  const [sortDir, setSortDir] = React.useState('desc');
  const sortedRows = React.useMemo(() => {
    if (!sortKey) return rows;
    const arr = [...rows];
    const pick = (r) => {
      switch (sortKey) {
        case 'label': return (r.label || '').toLowerCase();
        case 'google': return r.google;
        case 'meta': return r.meta;
        case 'total': return r.total;
        case 'googleCost': return r.googleCost;
        case 'metaCost': return r.metaCost;
        case 'cost': return r.cost;
        case 'roas': return r.cost > 0 ? (r.bqRevenue || 0) / r.cost : 0;
        case 'bqRevenue': return r.bqRevenue || 0;
        default: return 0;
      }
    };
    arr.sort((a, b) => {
      const av = pick(a); const bv = pick(b);
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv, 'tr') : bv.localeCompare(av, 'tr');
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [rows, sortKey, sortDir]);
  const changeSort = (k, d) => { setSortKey(k); setSortDir(d); };

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl border border-purple-200 bg-purple-50 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800">Marka Bazlı Performans</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Kampanya isminde marka anahtar kelimesi geçenler platform bazında toplanır · BQ kolonları GA4 item_sales'ten
              {dateRangeLabel && <span className="text-gray-400"> — {dateRangeLabel}</span>}
            </p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <SortHeader sortKey="label" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} align="left" className="px-4 py-3 w-40">Marka</SortHeader>
              <SortHeader sortKey="google" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 border-l border-gray-200">Bütçe Google</SortHeader>
              <SortHeader sortKey="meta" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3">Bütçe Meta</SortHeader>
              <SortHeader sortKey="total" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3">Bütçe Toplam (% Pay)</SortHeader>
              <SortHeader sortKey="cost" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 border-l border-gray-200">Harcama (% Pay)</SortHeader>
              <SortHeader sortKey="bqRevenue" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 border-l border-gray-200">BQ Ciro</SortHeader>
              <SortHeader sortKey="roas" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 w-20">ROAS</SortHeader>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="py-8 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!loading && sortedRows.map((r) => {
              const isEmpty = r.campaigns === 0;
              const bqRev = Number(r.bqRevenue) || 0;
              const roas = r.cost > 0 ? bqRev / r.cost : 0;
              return (
                <tr
                  key={r.key}
                  className={`border-t border-gray-100 hover:bg-gray-50/50 ${isEmpty ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-3 font-medium text-gray-800">{r.label}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700 border-l border-gray-100">
                    {r.google > 0 ? fmtMoney(r.google) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                    {r.meta > 0 ? fmtMoney(r.meta) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                    {r.total > 0 ? (
                      <span>{fmtMoney(r.total)} <span className="text-gray-400 text-xs">({fmtPct(r.total, totals.total)})</span></span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700 border-l border-gray-100">
                    {r.cost > 0 ? (
                      <span>{fmtMoney(r.cost)} <span className="text-gray-400 text-xs">({fmtPct(r.cost, totals.cost)})</span></span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-indigo-700 border-l border-gray-100">
                    {bqRev > 0 ? fmtMoney(bqRev) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-xs text-gray-700">
                    {roas > 0 ? `${roas.toFixed(2)}x` : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              );
            })}

            {!loading && hasOther && (
              <tr className="border-t border-gray-100 bg-gray-50/40 text-gray-500 italic">
                <td className="px-4 py-3">Diğer (eşleşmeyen)</td>
                <td className="px-4 py-3 text-right tabular-nums border-l border-gray-100">{other.google > 0 ? fmtMoney(other.google) : '—'}</td>
                <td className="px-4 py-3 text-right tabular-nums">{other.meta > 0 ? fmtMoney(other.meta) : '—'}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {other.total > 0 ? <span>{fmtMoney(other.total)} <span className="text-gray-400 text-xs">({fmtPct(other.total, totals.total)})</span></span> : '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums border-l border-gray-100">
                  {other.cost > 0 ? <span>{fmtMoney(other.cost)} <span className="text-gray-400 text-xs">({fmtPct(other.cost, totals.cost)})</span></span> : '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums border-l border-gray-100">—</td>
                <td className="px-4 py-3 text-right tabular-nums text-xs">—</td>
              </tr>
            )}

            {!loading && (
              <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                <td className="px-4 py-3 text-gray-800">TOPLAM</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900 border-l border-gray-100">{fmtMoney(totals.google)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtMoney(totals.meta)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                  {fmtMoney(totals.total)} {totals.total > 0 && <span className="text-gray-500 text-xs">(100%)</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-800 border-l border-gray-100">
                  {fmtMoney(totals.cost)} {totals.cost > 0 && <span className="text-gray-500 text-xs">(100%)</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-indigo-800 border-l border-gray-100">
                  {totals.bqRevenue > 0 ? fmtMoney(totals.bqRevenue) : '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900 text-xs">
                  {totals.cost > 0 ? `${(totals.bqRevenue / totals.cost).toFixed(2)}x` : '—'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Per-platform section: dedicated card + table for Google OR Meta.
// ---------------------------------------------------------------------------
// Sortable column header — clicking cycles asc → desc → unsorted.
function SortHeader({ sortKey, currentKey, currentDir, onChange, className = '', children, align = 'right' }) {
  const active = sortKey === currentKey;
  const Icon = !active ? ArrowUpDown : currentDir === 'asc' ? ArrowUp : ArrowDown;
  const handleClick = () => {
    if (!active) return onChange(sortKey, 'desc'); // first click: desc (biggest first)
    if (currentDir === 'desc') return onChange(sortKey, 'asc');
    return onChange(null, null); // third click clears sort
  };
  return (
    <th className={`${className} cursor-pointer select-none group`} onClick={handleClick}>
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
        <span>{children}</span>
        <Icon className={`w-3 h-3 transition-colors ${active ? 'text-blue-600' : 'text-gray-300 group-hover:text-gray-500'}`} />
      </div>
    </th>
  );
}

function PlatformSection({
  platformKey,
  loading,
  campaigns,
  expanded,
  onToggle,
  onExpandAll,
  onCollapseAll,
  cached,
  dateRangeLabel,
}) {
  const badge = PLATFORM_BADGE[platformKey] || PLATFORM_BADGE.GOOGLE_ADS;
  const totalBudget = campaigns.reduce((s, c) => s + (Number(c.effectiveDailyBudget) || 0), 0);
  const totalCost = campaigns.reduce((s, c) => s + (Number(c.cost) || 0), 0);
  const totalConv = campaigns.reduce((s, c) => s + (Number(c.conversions) || 0), 0);
  const totalRev = campaigns.reduce((s, c) => s + (Number(c.revenue) || 0), 0);
  const childTerm = platformKey === 'META' ? 'Ad Set' : 'Ad Group';

  // Sort state — per-section (each PlatformSection keeps its own so Google and
  // Meta tables can be sorted independently).
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState(null);
  const sortedCampaigns = React.useMemo(() => {
    if (!sortKey) return campaigns;
    const arr = [...campaigns];
    const pick = (c) => {
      switch (sortKey) {
        case 'name': return (c.name || '').toLowerCase();
        case 'status': return (c.status || '').toLowerCase();
        case 'budget': return Number(c.effectiveDailyBudget) || 0;
        case 'cost': return Number(c.cost) || 0;
        case 'conversions': return Number(c.conversions) || 0;
        case 'revenue': return Number(c.revenue) || 0;
        case 'roas': {
          const cost = Number(c.cost) || 0;
          return cost > 0 ? (Number(c.revenue) || 0) / cost : 0;
        }
        case 'children': return (c.children || []).length;
        default: return 0;
      }
    };
    arr.sort((a, b) => {
      const av = pick(a);
      const bv = pick(b);
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv, 'tr') : bv.localeCompare(av, 'tr');
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [campaigns, sortKey, sortDir]);
  const changeSort = (key, dir) => { setSortKey(key); setSortDir(dir); };

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/80 to-white">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-xl border flex items-center justify-center ${badge.cls}`}>
            <span className="font-bold text-sm">{badge.label[0]}</span>
          </div>
          <div>
            <h2 className="font-semibold text-gray-800">
              {platformKey === 'GOOGLE_ADS' ? 'Google Ads' : 'Meta Ads'} Kampanyaları
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
              <span className="inline-flex items-center gap-1">
                <Filter className="w-3 h-3" /> {campaigns.length} kampanya
              </span>
              <span>Bütçe: <b className="text-gray-700">{fmtMoney(totalBudget)}</b></span>
              <span>Harcama: <b className="text-emerald-700">{fmtMoney(totalCost)}</b></span>
              <span>Conv: <b className="text-amber-700">{fmtNumber(totalConv)}</b></span>
              <span>Revenue: <b className="text-purple-700">{fmtMoney(totalRev)}</b></span>
              {dateRangeLabel && <span className="text-gray-400">({dateRangeLabel})</span>}
              {cached && <span className="text-gray-400">(cached)</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExpandAll}
            className="text-xs px-2 py-1 rounded-lg border border-gray-200 hover:bg-white text-gray-600"
          >
            Hepsini Aç
          </button>
          <button
            onClick={onCollapseAll}
            className="text-xs px-2 py-1 rounded-lg border border-gray-200 hover:bg-white text-gray-600"
          >
            Hepsini Kapat
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th className="w-8"></th>
              <SortHeader sortKey="name" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} align="left" className="px-4 py-3">Kampanya</SortHeader>
              <SortHeader sortKey="status" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} align="left" className="px-4 py-3 w-28">Durum</SortHeader>
              <SortHeader sortKey="budget" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 w-32">Günlük Bütçe</SortHeader>
              <SortHeader sortKey="cost" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 w-32">Harcama</SortHeader>
              <SortHeader sortKey="conversions" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 w-24">Conv.</SortHeader>
              <SortHeader sortKey="revenue" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 w-32">Revenue</SortHeader>
              <SortHeader sortKey="roas" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 w-20">ROAS</SortHeader>
              <SortHeader sortKey="children" currentKey={sortKey} currentDir={sortDir} onChange={changeSort} className="px-4 py-3 w-20">{childTerm}</SortHeader>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="py-12 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!loading && campaigns.length === 0 && (
              <tr><td colSpan={9} className="py-12 text-center text-gray-400">
                Bu platformda gösterilecek kampanya yok.
              </td></tr>
            )}
            {!loading && sortedCampaigns.map((c) => {
              const key = `${c.platform}:${c.id}`;
              const isOpen = expanded.has(key);
              const children = c.children || [];
              return (
                <React.Fragment key={key}>
                  <tr className="border-t border-gray-100 hover:bg-gray-50/50">
                    <td className="px-2 py-3">
                      {children.length > 0 ? (
                        <button
                          onClick={() => onToggle(key)}
                          className="w-6 h-6 rounded hover:bg-gray-100 inline-flex items-center justify-center text-gray-500"
                          aria-label={isOpen ? 'Kapat' : 'Aç'}
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      ) : (
                        <span className="inline-block w-6" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800 truncate max-w-md" title={c.name}>
                        {c.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {c.type || c.objective || ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">{statusBadge(c.status)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">
                      <div>{fmtMoney(c.effectiveDailyBudget)}</div>
                      {c.budgetSource === 'adset_sum' && (
                        <div className="text-[10px] font-normal text-indigo-600 mt-0.5">ad set toplamı</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700">
                      {c.cost != null ? fmtMoney(c.cost) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-700">
                      {c.conversions != null ? fmtNumber(c.conversions) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-purple-700">
                      {c.revenue != null ? fmtMoney(c.revenue) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-xs text-gray-700">
                      {(() => {
                        const cost = Number(c.cost) || 0;
                        if (cost <= 0) return <span className="text-gray-300">—</span>;
                        const roas = (Number(c.revenue) || 0) / cost;
                        return `${roas.toFixed(2)}x`;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {children.length > 0 ? children.length : '—'}
                    </td>
                  </tr>

                  {/* Children rows — only ad group/adset name + budget are
                      shown. Cost/conv/revenue at child level aren't fetched
                      live (too many API calls) so those cells show —. */}
                  {isOpen && children.map((ch) => (
                    <tr key={`${key}-${ch.id}`} className="bg-gray-50/40 border-t border-gray-100 text-[13px]">
                      <td></td>
                      <td className="px-4 py-2" colSpan={2}>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] uppercase text-gray-400 font-medium">{childTerm}</span>
                          <div className="pl-2 border-l-2 border-gray-200 text-gray-700 truncate max-w-md" title={ch.name}>
                            {ch.name}
                          </div>
                          <span className="text-xs">{statusBadge(ch.status)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
                        {ch.dailyBudget != null ? fmtMoney(ch.dailyBudget) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-300">—</td>
                      <td className="px-4 py-2 text-right text-gray-300">—</td>
                      <td className="px-4 py-2 text-right text-gray-300">—</td>
                      <td className="px-4 py-2 text-right text-gray-300">—</td>
                      <td></td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AYLIK HEDEF BÜTÇE vs GERÇEKLEŞEN
// ---------------------------------------------------------------------------
// Hedefler backend config'inden (müşteri e-postası), gerçekleşen DB'den gelir.
// Marka kırılımı BURADA yapılır: uç ham kampanya satırlarını döner, kovalama
// brandGroups.js + matchBrand ile — sayfanın geri kalanıyla aynı tek kaynak.
//
// 🔑 Ay içindeyken "hedefin altındayız" demek yanıltıcı: 31 günün 3'ü geçmişken
// %6 harcanmış olması normaldir. Bu yüzden iki ayrı ölçü gösterilir:
//   • Ay sonu hedefine göre ilerleme (harcanan / aylık hedef)
//   • Bugüne kadarki BEKLENEN'e göre sapma (harcanan / hedef × geçen gün oranı)
// Renk/durum ikincisine bakar; birincisi sadece bilgi.
// Tek bir kırılımın tablosu. Dördü (kanal / Google / Meta / marka) aynı anda,
// alt alta gösterilir — sekmeyle tek tek değil (efe, 4 Ağu).
function BudgetPlanTable({ title, subtitle, rows, paceRatio, note }) {
  const totals = rows.reduce(
    (a, r) => ({ target: a.target + r.target, spend: a.spend + r.spend }),
    { target: 0, spend: 0 }
  );

  // Ay içindeyken ham hedefle kıyas yanıltır; sapma "bugüne kadar beklenen"e göre.
  const statusOf = (spend, target) => {
    const expected = target * paceRatio;
    if (!(target > 0)) return { tone: 'gray', text: 'hedef yok' };
    if (expected <= 0) return { tone: 'gray', text: '—' };
    const idx = spend / expected;
    if (idx >= 1.15) return { tone: 'red', text: `beklenenin %${Math.round((idx - 1) * 100)} üstünde` };
    if (idx >= 1.05) return { tone: 'amber', text: `%${Math.round((idx - 1) * 100)} hızlı` };
    if (idx <= 0.5) return { tone: 'red', text: `beklenenin %${Math.round((1 - idx) * 100)} altında` };
    if (idx <= 0.9) return { tone: 'amber', text: `%${Math.round((1 - idx) * 100)} geride` };
    return { tone: 'emerald', text: 'planda' };
  };
  const TONE = {
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    gray: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className="border-t">
      <div className="px-5 pt-4 pb-2 bg-white">
        <h4 className="text-sm font-bold text-gray-900">{title}</h4>
        {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-y">
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Kırılım</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Hedef</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Gerçekleşen</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  title="Bugüne kadar bu kadar harcanmış olmalıydı (aylık hedef × ayın geçen kısmı)">
                Bugüne kadar beklenen
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Fark</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ay sonu hedefine ilerleme</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Durum</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const expected = r.target * paceRatio;
              const diff = r.spend - expected;
              const progress = r.target > 0 ? (r.spend / r.target) : 0;
              const st = statusOf(r.spend, r.target);
              return (
                <tr key={r.key} className="border-b last:border-0 hover:bg-gray-50/60">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-800">{r.label}</div>
                    <div className="text-[11px] text-gray-400">
                      planda %{r.pct}{r.note ? ` · ${r.note}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{fmtMoney(r.target)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmtMoney(r.spend)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{fmtMoney(expected)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${diff >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    {diff >= 0 ? '+' : ''}{fmtMoney(diff)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden min-w-[80px] relative">
                        <div className="h-full rounded-full bg-indigo-500"
                             style={{ width: `${Math.min(100, progress * 100)}%` }} />
                        {/* Ayın geçen kısmı — çubuk bu çizgiyi geçtiyse hızlı gidiyor */}
                        <div className="absolute top-0 bottom-0 w-px bg-gray-500/70"
                             style={{ left: `${Math.min(100, paceRatio * 100)}%` }}
                             title={`Ayın geçen kısmı: %${Math.round(paceRatio * 100)}`} />
                      </div>
                      <span className="text-xs tabular-nums text-gray-600 w-11 text-right">
                        %{Math.round(progress * 100)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${TONE[st.tone]}`}>
                      {st.text}
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-gray-50 font-semibold">
              <td className="px-4 py-2.5 text-gray-900">TOPLAM</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{fmtMoney(totals.target)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">{fmtMoney(totals.spend)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{fmtMoney(totals.target * paceRatio)}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums ${totals.spend - totals.target * paceRatio >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                {totals.spend - totals.target * paceRatio >= 0 ? '+' : ''}{fmtMoney(totals.spend - totals.target * paceRatio)}
              </td>
              <td className="px-4 py-2.5 text-gray-600 text-xs">
                %{totals.target > 0 ? Math.round((totals.spend / totals.target) * 100) : 0} · ay %{Math.round(paceRatio * 100)} geçti
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      {note && (
        <div className="px-5 py-2 bg-gray-50/60 text-[11px] text-gray-500 leading-relaxed">{note}</div>
      )}
    </div>
  );
}

function BudgetPlanCard({ data, loading, onRefresh, onChangeMonth, brandGroups }) {
  const period = data?.period || {};
  const plan = data?.plan || null;
  const actual = data?.actual || null;

  // Ayın kaçta kaçı geçti — beklenen harcama bununla ölçeklenir
  const paceRatio = period.daysInMonth > 0 ? (period.elapsedDays || 0) / period.daysInMonth : 0;

  // Marka kovaları: kampanya adı → marka. "Diğer" = plandaki 4 ana marka dışı.
  const brandActual = useMemo(() => {
    const out = {};
    if (!data?.campaigns || !plan?.brands) return out;
    const named = plan.brands.filter((b) => b.key !== 'OTHER');
    plan.brands.forEach((b) => { out[b.key] = 0; });
    data.campaigns.forEach((c) => {
      const group = (brandGroups || []).find((g) => matchBrand(c.name, g.match || g.key));
      const hit = group ? named.find((b) => b.key === group.key) : null;
      out[hit ? hit.key : 'OTHER'] += Number(c.spend) || 0;
    });
    return out;
  }, [data, plan, brandGroups]);

  const sections = useMemo(() => {
    if (!plan || !actual) return [];
    return [
      {
        id: 'channel',
        title: 'Kanal bazlı',
        subtitle: 'Google / Meta / RTB — üst seviye bütçe dağılımı',
        rows: plan.channels.map((c) => ({
          key: c.key, label: c.label, pct: c.pct, target: c.target,
          spend: Number(actual.channels?.[c.key] || 0),
        })),
      },
      {
        id: 'google',
        title: 'Google kırılımı',
        subtitle: 'Search / PMax / Other — kampanya tipinden',
        rows: plan.google.map((g) => ({
          key: g.key, label: g.label, pct: g.pct, target: g.target,
          spend: Number(actual.google?.[g.key] || 0),
        })),
      },
      {
        id: 'meta',
        title: 'Meta kırılımı',
        subtitle: 'Conversion / Retargeting / Traffic',
        note: 'Conversion–Retargeting ayrımı kampanya adındaki “/RM” ekinden gelir; Ağustos’ta tüm kampanyalar OUTCOME_SALES hedefli olduğu için objective alanından ayrılamıyor.',
        rows: plan.meta.map((m) => ({
          key: m.key, label: m.label, pct: m.pct, target: m.target,
          spend: Number(actual.meta?.[m.key] || 0),
        })),
      },
      {
        id: 'brand',
        title: 'Marka bazlı',
        subtitle: 'Google + Meta harcaması birlikte',
        note: 'Marka kırılımı kampanya adından eşlenir (aynı liste: brandGroups.js); markasız/karma kampanyalar (ör. SMARTSHOPPING, GLOWYTICSSTARPRODUCTS) “Diğer” kovasına düşer.',
        rows: plan.brands.map((b) => ({
          key: b.key, label: b.label, pct: b.pct, target: b.target, note: b.note,
          spend: Number(brandActual[b.key] || 0),
        })),
      },
    ];
  }, [plan, actual, brandActual]);

  // Hiç veri yoksa (planı olmayan marka) kart tümden gizlenir. Ama plan bir kez
  // yüklendikten sonra kullanıcı plansız bir aya geçerse kart KAYBOLMAMALI —
  // yoksa ay seçici de gidiyor ve geri dönmenin yolu kalmıyor.
  const [everHadPlan, setEverHadPlan] = useState(false);
  useEffect(() => { if (data?.hasPlan) setEverHadPlan(true); }, [data]);
  if (!loading && !data) return null;
  if (!loading && data?.hasPlan === false && !everHadPlan) return null;

  const monthName = TR_MONTHS[(data?.month || 1) - 1];

  return (
    <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
      <div className="p-5 bg-white">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {monthName} {data?.year} · Hedef Bütçe Dağılımı vs Gerçekleşen
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {period.startDate} → {period.lastDataDate || '—'} · ayın{' '}
                <strong>{period.elapsedDays || 0}/{period.daysInMonth || 0}</strong> günü geçti
                {plan?.source ? ` · hedef kaynağı: ${plan.source}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={`${data?.year}-${data?.month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                onChangeMonth?.(y, m);
              }}
              className="px-3 py-2 bg-white border rounded-xl text-sm"
            >
              {[7, 8].map((m) => (
                <option key={m} value={`2026-${m}`}>{TR_MONTHS[m - 1]} 2026</option>
              ))}
            </select>
            <button type="button" onClick={onRefresh} disabled={loading}
              className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Planın kendi içindeki tutarsızlıklar — sessizce yutma */}
        {Array.isArray(data?.warnings) && data.warnings.length > 0 && (
          <div className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <strong>Gelen planda aritmetik tutarsızlık var</strong> (tutarlar esas alındı, yüzdeler değil):
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          </div>
        )}
        {Array.isArray(data?.channelCheck) && data.channelCheck.some((c) => c.matches === false) && (
          <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            <strong>Dikkat:</strong> plan dosyasındaki kanal hedefi ile Aylık Hedefler tablosundaki
            değer ayrışmış — biri elle değiştirilmiş olabilir.
          </div>
        )}

        <div className="mt-3 text-[11px] text-gray-500 leading-relaxed">
          <strong>Nasıl okunur:</strong> “Fark” bugüne kadar beklenene göredir, ay sonu hedefine göre değil —
          ayın %{Math.round(paceRatio * 100)}'i geçtiği için ham hedefle kıyaslamak yanıltır.
          Çubuktaki dikey çizgi ayın geçen kısmını gösterir; çubuk çizgiyi geçtiyse plandan hızlı harcanıyor.
        </div>
      </div>

      {loading && <div className="p-8 text-center text-gray-400 text-sm border-t">Yükleniyor…</div>}

      {!loading && data?.hasPlan === false && (
        <div className="p-8 text-center text-sm text-gray-500 border-t">
          {monthName} {data?.year} için hedef bütçe planı girilmemiş.
          <div className="text-xs text-gray-400 mt-1">
            Plan `glowytics-backend/src/config/monthlyBudgetPlans.js` dosyasında tutuluyor.
          </div>
        </div>
      )}

      {!loading && sections.map((s) => (
        <BudgetPlanTable
          key={s.id}
          title={s.title}
          subtitle={s.subtitle}
          rows={s.rows}
          paceRatio={paceRatio}
          note={s.note}
        />
      ))}
    </Card>
  );
}
